import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import RoadmapItemEditor from './RoadmapItemEditor';
import { ROADMAP_STATUS_COLORS } from '../utils/chartUtils';
import { sanitizeText } from '../utils/sanitization';

// Feature Flags
const ENABLE_KANBAN_VIEW = false; // Feature flag para habilitar/desabilitar visualização Kanban

// Função para formatação consistente de datas em pt-BR usando UTC
function formatDateBR(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '-';
  const day = date.getUTCDate().toString().padStart(2, '0');
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
}
import RoadmapScaleSelector from "./RoadmapScaleSelector";

// Função utilitária para converter string de data para objeto Date
function parseDate(dateStr) {
  if (!dateStr) return null;
  if (typeof dateStr === 'number') {
    // Trata como serial Excel (dias desde 1899-12-30)
    // Usando UTC para evitar problemas de fuso horário
    const excelEpoch = new Date(Date.UTC(1899, 11, 30)); // 30 de dezembro de 1899 (UTC)
    const resultDate = new Date(excelEpoch.getTime() + dateStr * 24 * 60 * 60 * 1000);
    // Ajusta para meio-dia UTC para evitar problemas de fuso horário
    return new Date(Date.UTC(resultDate.getUTCFullYear(), resultDate.getUTCMonth(), resultDate.getUTCDate(), 12, 0, 0));
  }
  if (dateStr instanceof Date) return dateStr;
  if (typeof dateStr !== 'string') return null;
  // Aceita formatos YYYY-MM-DD, DD/MM/YYYY, etc.
  const parts = dateStr.includes('/') ? dateStr.split('/') : dateStr.split('-');
  if (parts.length === 3) {
    if (dateStr.includes('/')) {
      // DD/MM/YYYY - cria data no meio-dia UTC para evitar problemas de fuso horário
      return new Date(Date.UTC(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]), 12, 0, 0));
    } else {
      // YYYY-MM-DD - cria data no meio-dia UTC para evitar problemas de fuso horário
      return new Date(Date.UTC(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 12, 0, 0));
    }
  }
  // Fallback para outros formatos
  const fallbackDate = new Date(dateStr);
  if (!isNaN(fallbackDate.getTime())) {
    // Se a data é válida, ajusta para meio-dia UTC
    return new Date(Date.UTC(fallbackDate.getUTCFullYear(), fallbackDate.getUTCMonth(), fallbackDate.getUTCDate(), 12, 0, 0));
  }
  return null;
}

// Função para obter cor da barra do projeto
function getProjectBarColor(item, barType = 'main', customColors = {}) {
  // Cor para barra de atraso
  if (barType === 'delay') {
    return '#ef4444'; // vermelho para atraso
  }
  
  // Cor padrão baseada no status
  return getProjectColor(item, customColors);
}
function getStatusColor(status) {
  // Gera um número baseado no texto do status
  let hash = 0;
  for (let i = 0; i < status.length; i++) {
    hash = status.charCodeAt(i) + ((hash << 5) - hash);
  }
  // Gera um valor de cor entre 0 e 360
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 60%, 48%)`;
}

// Função para cor da barra baseada no Status do item
function getProjectColor(item, customColors = {}) {
  const status = item.status || item.Status || item['Status'];
  
  // 1ª prioridade: Cores customizadas pelo usuário
  if (status && customColors[status]) {
    return customColors[status];
  }
  
  // 2ª prioridade: Usa a cor padrão do status se existir
  if (status && ROADMAP_STATUS_COLORS[status]) {
    return ROADMAP_STATUS_COLORS[status];
  }
  
  // 3ª prioridade: Fallback para casos sem status definido
  return '#64748b'; // Cinza
}

// Função para obter a cor do filtro baseada no status
function getFilterColorForStatus(status, data, customColors = {}) {
  // 1ª prioridade: Cores customizadas pelo usuário
  if (status && customColors[status]) {
    return customColors[status];
  }
  
  // 2ª prioridade: Usa a cor padrão do status se existir
  if (status && ROADMAP_STATUS_COLORS[status]) {
    return ROADMAP_STATUS_COLORS[status];
  }
  
  // 3ª prioridade: Fallback para status não definidos
  return '#64748b'; // Cinza
}

// Tooltip React simples
function Tooltip({ visible, x, y, children }) {
  if (!visible) return null;
  return (
    <div
      className="fixed z-50 px-4 py-3 rounded-xl shadow-xl text-white pointer-events-none backdrop-blur-md border border-white/20"
      style={{
        left: x + 12,
        top: y + 12,
        minWidth: 180,
        maxWidth: 320,
        fontFamily: "'Inter', sans-serif",
        fontSize: '0.875rem',
        fontWeight: 500,
        letterSpacing: '0',
        lineHeight: 1.25,
        whiteSpace: 'pre-line',
        background: 'linear-gradient(135deg, rgba(30,41,59,0.95), rgba(51,65,85,0.95))',
      }}
    >
      {children}
    </div>
  );
}

// Componente para botão de expand/collapse
const ExpandCollapseButton = ({ isExpanded, hasChildren, onClick, itemName }) => {
  if (!hasChildren) {
    return null;
  }
  
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="w-4 h-4 rounded-sm flex items-center justify-center text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-all mr-1 flex-shrink-0"
      title={`${isExpanded ? 'Colapsar' : 'Expandir'} ${itemName}`}
    >
      <svg 
        className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
};

function RoadmapChart({ 
  data, 
  onEditItem, 
  onRemoveItem, 
  plausible,
  hasUnsavedChanges,
  currentRoadmapName,
  currentRoadmapId,
  onSave,
  isSaving,
  onMyRoadmaps
}) {
  // Estado para escala do cabeçalho
  const [scale, setScale] = useState('month'); // 'month', 'quarter', 'semester', 'year'
  
  // Estados para calendário contínuo
  const [baseDate, setBaseDate] = useState(new Date()); // Data base para começar o calendário
  const [periodLength, setPeriodLength] = useState(12); // Número de períodos a mostrar
  
  // Estado para densidade ajustável
  const [density, setDensity] = useState('comfortable'); // 'compact', 'comfortable', 'spacious'
  
  // Estado para controlar expansão/colapso dos filtros
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Ajusta o período padrão quando a escala muda
  useEffect(() => {
    if (scale === 'month') setPeriodLength(12);
    else if (scale === 'quarter') setPeriodLength(2); // Começar com 2 trimestres por padrão
    else if (scale === 'semester') setPeriodLength(4);
    else if (scale === 'year') setPeriodLength(3);
  }, [scale]);

  // Atalho de teclado para expandir/colapsar filtros (tecla F)
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Verifica se o foco está em um input ou textarea para evitar interferências
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }
      
      if (event.key.toLowerCase() === 'f' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        setFiltersExpanded(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Função para obter configurações de densidade
  const getDensityConfig = () => {
    const configs = {
      compact: {
        rowHeight: 44,  // Aumentado de 40 para 44
        headerHeight: 44,  // Aumentado de 40 para 44
        barHeight: 20,
        fontSize: {
          project: 10,
          activity: 9, 
          subactivity: 8,
          header: 9
        },
        padding: {
          horizontal: 12,
          vertical: 2,  // Aumentado de 1 para 2
          subitemIndent: 20
        },
        icon: '📐'
      },
      comfortable: {
        rowHeight: 56,
        headerHeight: 48,
        barHeight: 24,
        fontSize: {
          project: 12,
          activity: 11,
          subactivity: 10, 
          header: 11
        },
        padding: {
          horizontal: 16,
          vertical: 2,
          subitemIndent: 24
        },
        icon: '📏'
      },
      spacious: {
        rowHeight: 72,
        headerHeight: 60,
        barHeight: 28,
        fontSize: {
          project: 14,
          activity: 13,
          subactivity: 12,
          header: 13
        },
        padding: {
          horizontal: 20,
          vertical: 4,
          subitemIndent: 28
        },
        icon: '📐'
      }
    };
    return configs[density];
  };

  // Função para calcular e formatar o período visível atual
  const getCurrentPeriodInfo = () => {
    if (!minDate || !maxDate) return { period: '', isToday: false, position: 0 };
    
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 12, 0, 0));
    const isToday = today >= minDate && today <= maxDate;
    
    // Calcula posição atual como percentual
    const position = isToday ? getTodayLinePosition() : 0;
    
    let period = '';
    const startMonth = minDate.getUTCMonth();
    const startYear = minDate.getUTCFullYear();
    const endMonth = maxDate.getUTCMonth();
    const endYear = maxDate.getUTCFullYear();
    
    if (scale === 'month') {
      const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 
                         'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      if (startYear === endYear && startMonth === endMonth) {
        period = `${monthNames[startMonth]} ${startYear}`;
      } else if (startYear === endYear) {
        period = `${monthNames[startMonth]} - ${monthNames[endMonth]} ${startYear}`;
      } else {
        period = `${monthNames[startMonth]} ${startYear} - ${monthNames[endMonth]} ${endYear}`;
      }
    } else if (scale === 'quarter') {
      const getQuarter = (month) => Math.floor(month / 3) + 1;
      const startQ = getQuarter(startMonth);
      const endQ = getQuarter(endMonth);
      if (startYear === endYear && startQ === endQ) {
        period = `Q${startQ} ${startYear}`;
      } else if (startYear === endYear) {
        period = `Q${startQ} - Q${endQ} ${startYear}`;
      } else {
        period = `Q${startQ} ${startYear} - Q${endQ} ${endYear}`;
      }
    } else if (scale === 'semester') {
      const getSemester = (month) => month < 6 ? 1 : 2;
      const startS = getSemester(startMonth);
      const endS = getSemester(endMonth);
      if (startYear === endYear && startS === endS) {
        period = `${startS}º Sem ${startYear}`;
      } else if (startYear === endYear) {
        period = `${startS}º - ${endS}º Sem ${startYear}`;
      } else {
        period = `${startS}º Sem ${startYear} - ${endS}º Sem ${endYear}`;
      }
    } else if (scale === 'year') {
      if (startYear === endYear) {
        period = `${startYear}`;
      } else {
        period = `${startYear} - ${endYear}`;
      }
    }
    
    return { period, isToday, position };
  };
  
  // Timeline navigation via scroll only - drag functionality removed
  
  // Estados para redimensionamento de barras
  const [isResizing, setIsResizing] = useState(false);
  const [resizingItem, setResizingItem] = useState(null);
  const [resizeType, setResizeType] = useState(null); // 'start' ou 'end'
  const [resizeStartX, setResizeStartX] = useState(0);
  const [originalDates, setOriginalDates] = useState(null);
  
  // Estado para dados modificados pelo redimensionamento - persistir no localStorage
  const [modifiedData, setModifiedData] = useState(() => {
    try {
      const saved = localStorage.getItem('roadmap-modified-data');
      return saved ? JSON.parse(saved, (key, value) => {
        // Reconstrói objetos Date durante o parse
        if (key === 'start' || key === 'end' || key === 'dataConclusao' || key === 'previsaoEntrega') {
          return value ? new Date(value) : null;
        }
        return value;
      }) : {};
    } catch (error) {
      console.warn('Erro ao carregar dados modificados do localStorage:', error);
      return {};
    }
  });
  
  // Persiste modifiedData no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem('roadmap-modified-data', JSON.stringify(modifiedData));
    } catch (error) {
      console.warn('Erro ao salvar dados modificados no localStorage:', error);
    }
  }, [modifiedData]);
  
  // Timeline navigation via scroll only - drag functionality removed
  
  // Effect para forçar atualização das barras quando a timeline muda
  useEffect(() => {
    console.log('🔄 useEffect triggered - baseDate ou scale mudaram');
    console.log('📅 Current baseDate:', baseDate.toISOString().split('T')[0]);
    console.log('📏 Current scale:', scale);
    
    // Força uma re-renderização das barras quando baseDate muda
    // para garantir que o posicionamento seja recalculado
    if (chartContainerRef.current) {
      // Força um repaint das barras
      const barElements = chartContainerRef.current.querySelectorAll('[data-bar-element]');
      console.log('📊 Elementos de barra encontrados:', barElements.length);
      barElements.forEach(el => {
        el.style.transform = 'translateZ(0)';
        // Remove o transform no próximo frame para forçar re-renderização
        requestAnimationFrame(() => {
          el.style.transform = '';
        });
      });
    }
  }, [baseDate, scale]);
  
  // Funções para redimensionamento de barras
  const formatDateForDisplay = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  
  const handleResizeStart = (e, item, type) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    setResizingItem({ ...item });
    setResizeType(type);
    setResizeStartX(e.clientX);
    setOriginalDates({
      start: item.start,
      end: item.end || item.dataConclusao,
      previsaoEntrega: item.previsaoEntrega
    });
    
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  };
  
  const handleResizeMove = (e) => {
    if (!isResizing || !resizingItem) return;
    
    const deltaX = e.clientX - resizeStartX;
    const chartWidth = 1000; // Largura aproximada do gráfico
    
    // Calcula quantos dias foram arrastados baseado na escala
    let daysPerPixel;
    switch (scale) {
      case 'month':
        daysPerPixel = periodLength * 30 / chartWidth;
        break;
      case 'quarter':
        daysPerPixel = periodLength * 90 / chartWidth;
        break;
      case 'semester':
        daysPerPixel = periodLength * 180 / chartWidth;
        break;
      case 'year':
        daysPerPixel = periodLength * 365 / chartWidth;
        break;
      default:
        daysPerPixel = 1;
    }
    
    const daysDelta = Math.round(deltaX * daysPerPixel);
    
    // Cria uma cópia do item com as novas datas
    const updatedItem = { ...resizingItem };
    
    if (resizeType === 'start') {
      // Redimensionando o início
      const newStartDate = new Date(originalDates.start);
      newStartDate.setDate(newStartDate.getDate() + daysDelta);
      updatedItem.start = newStartDate;
    } else if (resizeType === 'end') {
      // Redimensionando o fim
      if (resizingItem.hasDelay && originalDates.previsaoEntrega) {
        // Se tem atraso, redimensiona a data de conclusão
        const newEndDate = new Date(originalDates.end);
        newEndDate.setDate(newEndDate.getDate() + daysDelta);
        updatedItem.end = newEndDate;
        updatedItem.dataConclusao = newEndDate;
      } else {
        // Se não tem atraso, redimensiona a previsão
        const newEndDate = new Date(originalDates.end);
        newEndDate.setDate(newEndDate.getDate() + daysDelta);
        updatedItem.end = newEndDate;
        updatedItem.previsaoEntrega = newEndDate;
      }
    }
    
    setResizingItem(updatedItem);
  };
  
  const handleResizeEnd = () => {
    if (!isResizing || !resizingItem) return;
    
    // Salva as mudanças no estado de dados modificados
    setModifiedData(prev => ({
      ...prev,
      [resizingItem.id]: {
        start: resizingItem.start,
        end: resizingItem.end,
        dataConclusao: resizingItem.dataConclusao,
        previsaoEntrega: resizingItem.previsaoEntrega,
        hasDelay: resizingItem.hasDelay
      }
    }));
    
    // Reset dos estados de redimensionamento
    setIsResizing(false);
    setResizingItem(null);
    setResizeType(null);
    setResizeStartX(0);
    setOriginalDates(null);
    
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };
  
  // Função helper para obter dados efetivos (originais + modificações)
  const getEffectiveItemData = (item) => {
    const modifications = modifiedData[item.id];
    if (!modifications) return item;
    
    const effectiveData = {
      ...item,
      start: modifications.start,
      end: modifications.end,
      dataConclusao: modifications.dataConclusao,
      previsaoEntrega: modifications.previsaoEntrega,
      hasDelay: modifications.hasDelay
    };
    
    return effectiveData;
  };

  // Calcula os limites absolutos dos dados para navegação
  const [absoluteMinDate, absoluteMaxDate] = useMemo(() => {
    if (!data || data.length === 0) {
      const today = new Date();
      return [
        new Date(today.getFullYear() - 1, 0, 1), // 1 ano atrás
        new Date(today.getFullYear() + 2, 11, 31) // 2 anos à frente
      ];
    }
    
    let min = null;
    let max = null;
    
    data.forEach(item => {
      const effectiveData = getEffectiveItemData(item);
      if (effectiveData.start) {
        if (!min || effectiveData.start < min) min = effectiveData.start;
      }
      if (effectiveData.end) {
        if (!max || effectiveData.end > max) max = effectiveData.end;
      }
      
      // Verifica subitens se existirem
      if (item.subitens && Array.isArray(item.subitens)) {
        item.subitens.forEach(subitem => {
          const effectiveSubData = getEffectiveItemData(subitem);
          if (effectiveSubData.start) {
            if (!min || effectiveSubData.start < min) min = effectiveSubData.start;
          }
          if (effectiveSubData.end) {
            if (!max || effectiveSubData.end > max) max = effectiveSubData.end;
          }
        });
      }
    });
    
    // Adiciona uma margem de segurança
    const today = new Date();
    const safeMin = min ? new Date(Math.min(min.getTime(), today.getTime() - (365 * 24 * 60 * 60 * 1000))) : new Date(today.getFullYear() - 1, 0, 1);
    const safeMax = max ? new Date(Math.max(max.getTime(), today.getTime() + (730 * 24 * 60 * 60 * 1000))) : new Date(today.getFullYear() + 2, 11, 31);
    
    return [safeMin, safeMax];
  }, [data, getEffectiveItemData]);

  // Estado para controlar se navegação manual está ativa
  const [isManualNavigation, setIsManualNavigation] = useState(false);

  // Funções de navegação temporal - sem limites
  const navigatePrevious = () => {
    console.log('🔙 NAVEGAÇÃO ANTERIOR INICIADA');
    console.log('📅 BaseDate atual:', baseDate.toISOString().split('T')[0]);
    console.log('📏 Escala atual:', scale);
    
    setIsManualNavigation(true);
    console.log('🔒 isManualNavigation definido como TRUE');
    
    const newBase = new Date(baseDate);
    
    if (scale === 'month') {
      newBase.setMonth(baseDate.getMonth() - 1);
      console.log('📅 Novo mês:', newBase.toISOString().split('T')[0]);
    } else if (scale === 'quarter') {
      newBase.setMonth(baseDate.getMonth() - 3);
      console.log('📅 Novo trimestre:', newBase.toISOString().split('T')[0]);
    } else if (scale === 'semester') {
      newBase.setMonth(baseDate.getMonth() - 6);
      console.log('📅 Novo semestre:', newBase.toISOString().split('T')[0]);
    } else if (scale === 'year') {
      newBase.setFullYear(baseDate.getFullYear() - 1);
      console.log('📅 Novo ano:', newBase.toISOString().split('T')[0]);
    }
    
    console.log('📅 Aplicando setBaseDate para:', newBase.toISOString().split('T')[0]);
    setBaseDate(newBase);
    
    // Reativa navegação temporal por scroll após um delay
    setTimeout(() => {
      setIsManualNavigation(false);
      console.log('🔓 isManualNavigation definido como FALSE após timeout');
    }, 1000);
  };
  
  const navigateNext = () => {
    console.log('▶️ NAVEGAÇÃO PRÓXIMA INICIADA');
    console.log('📅 BaseDate atual:', baseDate.toISOString().split('T')[0]);
    console.log('📏 Escala atual:', scale);
    
    setIsManualNavigation(true);
    console.log('🔒 isManualNavigation definido como TRUE');
    
    const newBase = new Date(baseDate);
    
    if (scale === 'month') {
      newBase.setMonth(baseDate.getMonth() + 1);
      console.log('📅 Novo mês:', newBase.toISOString().split('T')[0]);
    } else if (scale === 'quarter') {
      newBase.setMonth(baseDate.getMonth() + 3);
      console.log('📅 Novo trimestre:', newBase.toISOString().split('T')[0]);
    } else if (scale === 'semester') {
      newBase.setMonth(baseDate.getMonth() + 6);
      console.log('📅 Novo semestre:', newBase.toISOString().split('T')[0]);
    } else if (scale === 'year') {
      newBase.setFullYear(baseDate.getFullYear() + 1);
      console.log('📅 Novo ano:', newBase.toISOString().split('T')[0]);
    }
    
    console.log('📅 Aplicando setBaseDate para:', newBase.toISOString().split('T')[0]);
    setBaseDate(newBase);
    
    // Reativa navegação temporal por scroll após um delay
    setTimeout(() => {
      setIsManualNavigation(false);
      console.log('🔓 isManualNavigation definido como FALSE após timeout');
    }, 1000);
  };
  
  // Verifica se pode navegar - sempre verdadeiro (sem limites)
  const canNavigateNext = useMemo(() => {
    return true; // Navegação livre
  }, []);
  
  const canNavigatePrevious = useMemo(() => {
    return true; // Navegação livre
  }, []);
  
  // Effect para listeners globais de redimensionamento
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.addEventListener('mouseleave', handleResizeEnd);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.removeEventListener('mouseleave', handleResizeEnd);
    };
  }, [isResizing, resizeStartX, resizingItem, resizeType, originalDates]);
  
  // Effect para navegação por teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Apenas ativa se não estiver em input/textarea/select
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        return;
      }
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          navigatePrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          navigateNext();
          break;
        // Atalho "H" removido
        default:
          break;
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigatePrevious, navigateNext]);
  
  // Função para formatar o período atual exibido
  const getCurrentPeriodLabel = () => {
    if (!minDate || !maxDate) return '';
    const formatDate = (date) => {
      return date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
    };
    
    if (scale === 'month') {
      return `${formatDate(minDate)} - ${formatDate(maxDate)}`;
    } else if (scale === 'quarter') {
      const startQ = `Q${Math.floor(minDate.getMonth() / 3) + 1}/${minDate.getFullYear().toString().slice(-2)}`;
      const endQ = `Q${Math.floor(maxDate.getMonth() / 3) + 1}/${maxDate.getFullYear().toString().slice(-2)}`;
      return `${startQ} - ${endQ}`;
    } else if (scale === 'semester') {
      const startS = `${minDate.getMonth() < 6 ? '1º' : '2º'} Sem/${minDate.getFullYear().toString().slice(-2)}`;
      const endS = `${maxDate.getMonth() < 6 ? '1º' : '2º'} Sem/${maxDate.getFullYear().toString().slice(-2)}`;
      return `${startS} - ${endS}`;
    } else if (scale === 'year') {
      return `${minDate.getFullYear()} - ${maxDate.getFullYear()}`;
    }
    return '';
  };

  // Função utilitária para gerar labels do cabeçalho conforme escala
  function getHeaderLabels(startDate, endDate, scale) {
    console.log('🏷️ getHeaderLabels chamado para:', 
      startDate ? startDate.toISOString() : 'null',
      'até', 
      endDate ? endDate.toISOString() : 'null');
    
    if (!startDate || !endDate) {
      console.warn('⚠️ getHeaderLabels: startDate ou endDate é null - retornando array vazio');
      return [];
    }
    
    // CORREÇÃO: Garantir que estamos trabalhando com datas UTC consistentes
    const startUTC = new Date(Date.UTC(
      startDate.getUTCFullYear(),
      startDate.getUTCMonth(),
      scale === 'month' ? 1 : startDate.getUTCDate() // Para mês, sempre use dia 1
    ));
    
    const endUTC = new Date(Date.UTC(
      endDate.getUTCFullYear(),
      endDate.getUTCMonth(),
      endDate.getUTCDate(),
      23, 59, 59
    ));
    
    console.log('📅 startDate ajustado para UTC:', startUTC.toISOString());
    console.log('📅 endDate ajustado para UTC:', endUTC.toISOString());
    
    const labels = [];
    // CORREÇÃO: Use o objeto Date UTC que acabamos de criar
    const current = new Date(startUTC.getTime());
    console.log('📅 Current inicial para labels:', current.toISOString());
    
    let iterationCount = 0;
    const MAX_ITERATIONS = 100; // Segurança para evitar loops infinitos
    
    console.log('🔄 Iniciando loop de períodos para escala:', scale);
    
    if (scale === 'month') {
      while (current <= endUTC && iterationCount < MAX_ITERATIONS) {
        iterationCount++;
        const label = `${current.toLocaleString('pt-BR', { month: 'short' })}/${String(current.getFullYear()).slice(-2)}`;
        console.log(`📌 Iteração ${iterationCount} (mês): Adicionando label:`, label);
        labels.push(label);
        // CORREÇÃO: Usar setUTCMonth em vez de setMonth para consistência
        current.setUTCMonth(current.getUTCMonth() + 1);
      }
    } else if (scale === 'quarter') {
      while (current <= endUTC && iterationCount < MAX_ITERATIONS) {
        iterationCount++;
        const quarter = Math.floor(current.getUTCMonth() / 3) + 1;
        const label = `Q${quarter}/${String(current.getFullYear()).slice(-2)}`;
        console.log(`📌 Iteração ${iterationCount} (trimestre): Adicionando label:`, label);
        labels.push(label);
        // Avança para o próximo trimestre - CORRIGIDO para usar UTC
        current.setUTCMonth(current.getUTCMonth() + 3 - (current.getUTCMonth() % 3));
      }
    } else if (scale === 'semester') {
      while (current <= endUTC && iterationCount < MAX_ITERATIONS) {
        iterationCount++;
        const semester = current.getUTCMonth() < 6 ? 1 : 2;
        const label = `${semester}º Sem/${String(current.getFullYear()).slice(-2)}`;
        console.log(`📌 Iteração ${iterationCount} (semestre): Adicionando label:`, label);
        labels.push(label);
        // Avança para o próximo semestre - CORRIGIDO para usar UTC
        current.setUTCMonth(current.getUTCMonth() < 6 ? 6 : 0);
        if (current.getUTCMonth() === 0) {
          current.setUTCFullYear(current.getUTCFullYear() + 1);
        }
      }
    } else if (scale === 'year') {
      while (current <= endUTC && iterationCount < MAX_ITERATIONS) {
        iterationCount++;
        const label = `${current.getFullYear()}`;
        console.log(`📌 Iteração ${iterationCount} (ano): Adicionando label:`, label);
        labels.push(label);
        // CORREÇÃO: Usar setUTCFullYear em vez de setFullYear
        current.setUTCFullYear(current.getUTCFullYear() + 1);
      }
    }
    
    if (iterationCount >= MAX_ITERATIONS) {
      console.error('⚠️ ALERTA: Número máximo de iterações atingido em getHeaderLabels!');
    }
    
    console.log(`✅ getHeaderLabels concluído - ${labels.length} labels gerados:`, labels);
    return labels;
  }
  
  // Função utilitária para formatar label de um período baseado na escala
  function getFormatLabel(startDate, endDate, scale) {
    if (!startDate || !endDate) return '';
    
    console.log(`🏷️ getFormatLabel chamado para:`, startDate.toISOString(), 'até', endDate.toISOString());
    
    if (scale === 'month') {
      // CORREÇÃO: Usar UTC methods para consistência com as datas dos períodos
      const label = startDate.toLocaleString('pt-BR', { 
        month: 'short', 
        timeZone: 'UTC' 
      }) + '/' + String(startDate.getUTCFullYear()).slice(-2);
      console.log(`🏷️ Formato mês: ${label}`);
      return label;
    } else if (scale === 'quarter') {
      // CORREÇÃO: Usar UTC methods para consistência
      const quarter = Math.floor(startDate.getUTCMonth() / 3) + 1;
      const label = `Q${quarter}/${String(startDate.getUTCFullYear()).slice(-2)}`;
      console.log(`🏷️ Formato trimestre: ${label}`);
      return label;
    } else if (scale === 'semester') {
      // CORREÇÃO: Usar UTC methods para consistência
      const semester = startDate.getUTCMonth() < 6 ? 1 : 2;
      const label = `${semester}º Sem/${String(startDate.getUTCFullYear()).slice(-2)}`;
      console.log(`🏷️ Formato semestre: ${label}`);
      return label;
    } else if (scale === 'year') {
      // CORREÇÃO: Usar UTC methods para consistência
      const label = `${startDate.getUTCFullYear()}`;
      console.log(`🏷️ Formato ano: ${label}`);
      return label;
    }
    return '';
  }
  
  const scrollRef = useRef(null);
  const scrollContainerRef = useRef(null); // Para medir offset do container de scroll
  
  // Sistema unificado de controle de scroll e posicionamento sticky
  const [scrollState, setScrollState] = useState({
    scrollLeft: 0,
    isScrolling: false,
    containerOffsetLeft: 0 // Offset do container de scroll
  });
  
  // Estados para navegação temporal via scroll
  const [timelineScrollState, setTimelineScrollState] = useState({
    totalWidth: 0,
    visibleWidth: 0,
    scrollPercent: 0
  });
  
  // Constantes para navegação temporal
  const TEMPORAL_SCROLL_CONFIG = {
    PERIODS_BUFFER: 3, // Quantos períodos manter antes/depois da visualização
    SCROLL_SENSITIVITY: 0.1, // Sensibilidade do scroll para mudança de período
    MIN_SCROLL_DELTA: 50 // Mínimo de pixels para considerar mudança de período
  };
  
  // Constantes para o sistema de posicionamento sticky
  const STICKY_CONFIG = {
    HEADER_Z_INDEX: 60,
    ITEM_COLUMN_Z_INDEX: 50,
    TODAY_LINE_Z_INDEX: 40,
    CONTENT_Z_INDEX: 30,
    SHADOW_TRANSITION: 'box-shadow 0.2s ease-in-out',
    STICKY_SHADOW: '4px 0 12px -2px rgba(0,0,0,0.15)',
    DEFAULT_SHADOW: '2px 0 4px -2px rgba(0,0,0,0.08)'
  };
  
  // Função para calcular estilos sticky unificados
  const getStickyStyles = (baseStyles = {}) => {
    const hasScroll = scrollState.scrollLeft > 0;
    return {
      ...baseStyles,
      position: 'sticky',
      left: 0, // Sempre fixa na posição 0, não segue o scroll
      boxShadow: hasScroll ? STICKY_CONFIG.STICKY_SHADOW : STICKY_CONFIG.DEFAULT_SHADOW,
      transition: STICKY_CONFIG.SHADOW_TRANSITION,
      willChange: 'box-shadow' // Remove transform do willChange
    };
  };
  
  // Função para calcular timeline expandida para navegação contínua
  const getExpandedTimeline = useMemo(() => {
    const bufferPeriods = TEMPORAL_SCROLL_CONFIG.PERIODS_BUFFER;
    let expandedStart, expandedEnd;
    
    if (scale === 'month') {
      expandedStart = new Date(baseDate.getFullYear(), baseDate.getMonth() - bufferPeriods, 1);
      expandedEnd = new Date(baseDate.getFullYear(), baseDate.getMonth() + periodLength + bufferPeriods, 0, 23, 59, 59);
    } else if (scale === 'quarter') {
      const quarterStart = Math.floor(baseDate.getMonth() / 3) * 3;
      expandedStart = new Date(baseDate.getFullYear(), quarterStart - (bufferPeriods * 3), 1);
      const endYear = baseDate.getFullYear() + Math.floor((quarterStart + ((periodLength + bufferPeriods) * 3)) / 12);
      const endMonth = (quarterStart + ((periodLength + bufferPeriods) * 3)) % 12;
      expandedEnd = new Date(endYear, endMonth, 0, 23, 59, 59);
    } else if (scale === 'semester') {
      const semesterStart = baseDate.getMonth() < 6 ? 0 : 6;
      expandedStart = new Date(baseDate.getFullYear(), semesterStart - (bufferPeriods * 6), 1);
      const endYear = baseDate.getFullYear() + Math.floor((semesterStart + ((periodLength + bufferPeriods) * 6)) / 12);
      const endMonth = (semesterStart + ((periodLength + bufferPeriods) * 6)) % 12;
      expandedEnd = new Date(endYear, endMonth, 0, 23, 59, 59);
    } else if (scale === 'year') {
      expandedStart = new Date(baseDate.getFullYear() - bufferPeriods, 0, 1);
      expandedEnd = new Date(baseDate.getFullYear() + periodLength + bufferPeriods, 0, 0, 23, 59, 59);
    }
    
    return { expandedStart, expandedEnd };
  }, [baseDate, scale, periodLength]);
  
  // Timeline expandida com buffer para navegação contínua
  // Função para obter apenas os labels dos períodos visíveis (sem buffer)
  const visiblePeriodLabels = useMemo(() => {
    console.log('🔍 visiblePeriodLabels - Recalculando...');
    console.log('📊 Scale:', scale);
    console.log('📏 PeriodLength:', periodLength);
    console.log('📅 BaseDate:', baseDate.toISOString());
    
    // CORREÇÃO: Centraliza os períodos com base na data selecionada
    // Para isso, precisamos calcular quantos períodos exibir antes e depois da data base
    let visibleStart, visibleEnd;
    
    // Calculamos quanto devemos recuar para centralizar o período atual
    const periodsBeforeCurrent = Math.floor(periodLength / 2);
    // E quantos períodos após a data atual (incluindo o período atual)
    const periodsAfterCurrent = periodLength - periodsBeforeCurrent;
    
    console.log(`🔄 Distribuição de períodos: ${periodsBeforeCurrent} antes + ${periodsAfterCurrent} depois = ${periodLength} total`);
    
    if (scale === 'month') {
      // Recuar para centralizar
      const startMonth = baseDate.getUTCMonth() - periodsBeforeCurrent;
      const startYear = baseDate.getUTCFullYear() + Math.floor(startMonth / 12);
      const adjustedStartMonth = ((startMonth % 12) + 12) % 12; // Garante que o mês seja positivo
      
      visibleStart = new Date(Date.UTC(startYear, adjustedStartMonth, 1));
      
      // Avançar para completar o número de períodos - CORRIGIDO
      const endMonth = startMonth + periodLength - 1; // -1 porque startMonth já está incluído
      const endYear = baseDate.getUTCFullYear() + Math.floor(endMonth / 12);
      const adjustedEndMonth = ((endMonth % 12) + 12) % 12; // Garante que o mês seja positivo
      
      // O último dia do mês final
      visibleEnd = new Date(Date.UTC(endYear, adjustedEndMonth + 1, 0, 23, 59, 59));
      
      console.log(`🔢 Mês: Configurando ${periodLength} meses de visibilidade (centralizado)`);
    } else if (scale === 'quarter') {
      // Determina o trimestre atual
      const currentQuarter = Math.floor(baseDate.getUTCMonth() / 3);
      
      // Recuar para centralizar
      const startQuarter = currentQuarter - periodsBeforeCurrent;
      const startYear = baseDate.getUTCFullYear() + Math.floor(startQuarter / 4);
      const adjustedStartQuarter = ((startQuarter % 4) + 4) % 4; // Garante que o trimestre seja positivo
      const startMonth = adjustedStartQuarter * 3;
      
      visibleStart = new Date(Date.UTC(startYear, startMonth, 1));
      
      // Avançar para completar o número de períodos - CORRIGIDO
      const endQuarter = startQuarter + periodLength - 1; // -1 porque startQuarter já está incluído
      const endYear = baseDate.getUTCFullYear() + Math.floor(endQuarter / 4);
      const adjustedEndQuarter = ((endQuarter % 4) + 4) % 4;
      const endMonth = (adjustedEndQuarter * 3) + 2; // Último mês do trimestre
      
      // Último dia do último mês do trimestre
      visibleEnd = new Date(Date.UTC(endYear, endMonth + 1, 0, 23, 59, 59));
      
      console.log(`🔢 Trimestre: Configurando ${periodLength} trimestres de visibilidade (centralizado)`);
    } else if (scale === 'semester') {
      // Determina o semestre atual (0 = primeiro, 1 = segundo)
      const currentSemester = baseDate.getUTCMonth() < 6 ? 0 : 1;
      
      // Recuar para centralizar
      const startSemester = currentSemester - periodsBeforeCurrent;
      const startYear = baseDate.getUTCFullYear() + Math.floor(startSemester / 2);
      const adjustedStartSemester = ((startSemester % 2) + 2) % 2; // Garante que o semestre seja positivo
      const startMonth = adjustedStartSemester * 6;
      
      visibleStart = new Date(Date.UTC(startYear, startMonth, 1));
      
      // Avançar para completar o número de períodos - CORRIGIDO
      const endSemester = startSemester + periodLength - 1; // -1 porque startSemester já está incluído
      const endYear = baseDate.getUTCFullYear() + Math.floor(endSemester / 2);
      const adjustedEndSemester = ((endSemester % 2) + 2) % 2;
      const endMonth = (adjustedEndSemester * 6) + 5; // Último mês do semestre
      
      // Último dia do último mês do semestre
      visibleEnd = new Date(Date.UTC(endYear, endMonth + 1, 0, 23, 59, 59));
      
      console.log(`🔢 Semestre: Configurando ${periodLength} semestres de visibilidade (centralizado)`);
    } else if (scale === 'year') {
      // Recuar para centralizar
      const startYear = baseDate.getUTCFullYear() - periodsBeforeCurrent;
      visibleStart = new Date(Date.UTC(startYear, 0, 1));
      
      // Avançar para completar o número de períodos - CORRIGIDO
      const endYear = startYear + periodLength - 1; // -1 porque startYear já está incluído
      visibleEnd = new Date(Date.UTC(endYear, 11, 31, 23, 59, 59));
      
      console.log(`🔢 Ano: Configurando ${periodLength} anos de visibilidade (centralizado)`);
    }
    
    console.log('📅 visibleStart:', visibleStart?.toISOString());
    console.log('📅 visibleEnd:', visibleEnd?.toISOString());
    
    // Vamos calcular a duração em milissegundos para verificação
    if (visibleStart && visibleEnd) {
      const durationMs = visibleEnd.getTime() - visibleStart.getTime();
      const durationDays = durationMs / (1000 * 60 * 60 * 24);
      console.log(`📊 Duração em dias: ${durationDays.toFixed(2)}`);
      
      // Verificação extra para escala mensal (deve ser próximo de 30 * periodLength dias)
      if (scale === 'month') {
        const expectedDays = 30 * periodLength;
        console.log(`📊 Dias esperados para ${periodLength} meses: ~${expectedDays}`);
        if (Math.abs(durationDays - expectedDays) > periodLength * 5) {
          console.warn(`⚠️ ALERTA: Duração em dias (${durationDays.toFixed(2)}) muito diferente do esperado (~${expectedDays})`);
        }
      }
    }
    
    return { visibleStart, visibleEnd };
  }, [baseDate, scale, periodLength]);
  
  const expandedPeriodLabels = useMemo(() => {
    console.log('🔍 expandedPeriodLabels - Recalculando...');
    
    // Usamos apenas os períodos visíveis, não o expandido com buffer
    const { visibleStart, visibleEnd } = visiblePeriodLabels;
    console.log('📅 visibleStart recebido:', visibleStart?.toISOString());
    console.log('📅 visibleEnd recebido:', visibleEnd?.toISOString());
    
    if (!visibleStart || !visibleEnd) {
      console.warn('⚠️ visibleStart ou visibleEnd é null - retornando array vazio');
      return [];
    }
    
    // CORREÇÃO: Vamos verificar se estamos usando meses ou não para determinar se precisamos do dia 1
    let current;
    if (scale === 'month') {
      // Para meses, precisamos garantir que estamos começando do dia 1 do mês
      current = new Date(Date.UTC(visibleStart.getUTCFullYear(), visibleStart.getUTCMonth(), 1));
    } else {
      current = new Date(visibleStart);
    }
    console.log('📅 Current inicial (corrigido):', current.toISOString());
    console.log('🔄 Iniciando loop de períodos para escala:', scale);
    
    let intervals = [];
    let iterationCount = 0;
    const MAX_ITERATIONS = 100; // Segurança para evitar loops infinitos
    
    while (current <= visibleEnd && iterationCount < MAX_ITERATIONS) {
      iterationCount++;
      console.log(`📌 Iteração ${iterationCount}: current = ${current.toISOString()}`);
      
      let start = new Date(current.getTime());
      let end;
      
      if (scale === 'month') {
        end = new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + 1, 0, 23, 59, 59));
        current.setUTCMonth(current.getUTCMonth() + 1);
      } else if (scale === 'quarter') {
        const nextQuarter = current.getUTCMonth() + 3 - (current.getUTCMonth() % 3);
        end = new Date(Date.UTC(current.getUTCFullYear(), nextQuarter, 0, 23, 59, 59));
        current.setUTCMonth(nextQuarter);
      } else if (scale === 'semester') {
        if (current.getUTCMonth() < 6) {
          end = new Date(Date.UTC(current.getUTCFullYear(), 6, 0, 23, 59, 59));
          current.setUTCMonth(6);
        } else {
          end = new Date(Date.UTC(current.getUTCFullYear(), 12, 0, 23, 59, 59));
          current.setUTCFullYear(current.getUTCFullYear() + 1);
          current.setUTCMonth(0);
        }
      } else if (scale === 'year') {
        end = new Date(Date.UTC(current.getUTCFullYear() + 1, 0, 0, 23, 59, 59));
        current.setUTCFullYear(current.getUTCFullYear() + 1);
      }
      
      console.log(`📅 Período: ${start.toISOString()} até ${end.toISOString()}`);
      intervals.push({ start, end });
      
      console.log(`📅 Current após avanço: ${current.toISOString()}`);
      if (current > visibleEnd) {
        console.log('🛑 Condição de saída atingida: current > visibleEnd');
        break;
      }
    }
    
    if (iterationCount >= MAX_ITERATIONS) {
      console.error('⚠️ ALERTA: Número máximo de iterações atingido!');
    }
    
    console.log(`📊 Total de intervalos gerados: ${intervals.length}`);
    
    // Usa labels do visiblePeriodLabels
    try {
      const result = intervals.map((interval, index) => {
        try {
          const label = getFormatLabel(interval.start, interval.end, scale);
          console.log(`🏷️ Label ${index}: ${label}`);
          return { label, ...interval };
        } catch (err) {
          console.error(`❌ Erro ao formatar label para intervalo ${index}:`, err);
          console.log('🔍 Detalhes do intervalo:', JSON.stringify(interval));
          return { label: `Erro ${index}`, ...interval };
        }
      });
      
      console.log(`✅ expandedPeriodLabels concluído - ${result.length} períodos`);
      
      // DEBUG: Log específico dos períodos para análise
      console.log('📋 PERÍODOS GERADOS:');
      result.forEach((period, index) => {
        console.log(`${index}: ${period.label} (${period.start?.toISOString().split('T')[0]} - ${period.end?.toISOString().split('T')[0]})`);
      });
      
      return result;
    } catch (err) {
      console.error('❌ Erro ao processar intervalos:', err);
      return [];
    }
  }, [visiblePeriodLabels, scale]);
  
  // Constantes para layout
  const NAME_COL_WIDTH = 256; // px (w-64)
  const MIN_PERIOD_WIDTH = 120; // px
  
  // Estado para largura disponível do container
  const [availableWidth, setAvailableWidth] = useState(1200);
  
  // Effect para calcular largura disponível
  useEffect(() => {
    const updateAvailableWidth = () => {
      if (chartAreaRef.current) {
        const containerWidth = chartAreaRef.current.clientWidth;
        // Subtraindo a largura da coluna "Itens" para obter largura disponível para timeline
        setAvailableWidth(containerWidth - NAME_COL_WIDTH);
      }
    };

    updateAvailableWidth();
    window.addEventListener('resize', updateAvailableWidth);
    
    return () => window.removeEventListener('resize', updateAvailableWidth);
  }, []);

  // Função para calcular largura mínima dinâmica das colunas
  const getMinColumnWidth = useMemo(() => {
    const numPeriods = expandedPeriodLabels.length;
    
    // Se temos 12 meses ou menos, permitimos colunas menores para caber na tela
    if (scale === 'month' && periodLength <= 12 && numPeriods <= 12) {
      // availableWidth já tem NAME_COL_WIDTH subtraído
      return Math.max(60, Math.floor(availableWidth / numPeriods)); // Min 60px, distribuído igualmente
    }
    
    // Se temos 12 trimestres ou menos, permitimos colunas menores para caber na tela
    if (scale === 'quarter' && periodLength <= 12 && numPeriods <= 12) {
      // availableWidth já tem NAME_COL_WIDTH subtraído
      return Math.max(80, Math.floor(availableWidth / numPeriods)); // Min 80px para trimestres (mais largura que meses)
    }
    
    // Se temos 6 semestres ou menos, permitimos colunas menores para caber na tela
    if (scale === 'semester' && periodLength <= 6 && numPeriods <= 6) {
      // availableWidth já tem NAME_COL_WIDTH subtraído
      return Math.max(100, Math.floor(availableWidth / numPeriods)); // Min 100px para semestres (mais largura que trimestres)
    }
    
    // Se temos 10 anos ou menos, permitimos colunas menores para caber na tela
    if (scale === 'year' && periodLength <= 10 && numPeriods <= 10) {
      // availableWidth já tem NAME_COL_WIDTH subtraído
      return Math.max(120, Math.floor(availableWidth / numPeriods)); // Min 120px para anos (largura padrão)
    }
    
    return MIN_PERIOD_WIDTH; // Padrão 120px
  }, [expandedPeriodLabels.length, scale, periodLength, availableWidth]);

  // Largura total da timeline expandida - RESPONSIVA
  const expandedTimelineWidth = useMemo(() => {
    const numPeriods = expandedPeriodLabels.length;
    
    // Se temos 12 meses ou menos, força a usar toda a largura disponível
    if (scale === 'month' && periodLength <= 12 && numPeriods <= 12) {
      // availableWidth já tem NAME_COL_WIDTH subtraído
      console.log('🎯 MODO RESPONSIVO 12 MESES:');
      console.log('📊 Available Width (timeline only):', availableWidth);
      console.log('📊 Num Periods:', numPeriods);
      console.log('📊 Width per column:', Math.floor(availableWidth / numPeriods));
      return availableWidth; // Usa exatamente a largura disponível para a timeline, sem scroll
    }
    
    // Se temos 12 trimestres ou menos, força a usar toda a largura disponível
    if (scale === 'quarter' && periodLength <= 12 && numPeriods <= 12) {
      // availableWidth já tem NAME_COL_WIDTH subtraído
      console.log('🎯 MODO RESPONSIVO TRIMESTRES:');
      console.log('📊 Available Width (timeline only):', availableWidth);
      console.log('📊 Num Periods:', numPeriods);
      console.log('📊 Width per column:', Math.floor(availableWidth / numPeriods));
      return availableWidth; // Usa exatamente a largura disponível para a timeline, sem scroll
    }
    
    // Se temos 6 semestres ou menos, força a usar toda a largura disponível
    if (scale === 'semester' && periodLength <= 6 && numPeriods <= 6) {
      // availableWidth já tem NAME_COL_WIDTH subtraído
      console.log('🎯 MODO RESPONSIVO SEMESTRES:');
      console.log('📊 Available Width (timeline only):', availableWidth);
      console.log('📊 Num Periods:', numPeriods);
      console.log('📊 Width per column:', Math.floor(availableWidth / numPeriods));
      return availableWidth; // Usa exatamente a largura disponível para a timeline, sem scroll
    }
    
    // Se temos 10 anos ou menos, força a usar toda a largura disponível
    if (scale === 'year' && periodLength <= 10 && numPeriods <= 10) {
      // availableWidth já tem NAME_COL_WIDTH subtraído
      console.log('🎯 MODO RESPONSIVO ANOS:');
      console.log('📊 Available Width (timeline only):', availableWidth);
      console.log('📊 Num Periods:', numPeriods);
      console.log('📊 Width per column:', Math.floor(availableWidth / numPeriods));
      return availableWidth; // Usa exatamente a largura disponível para a timeline, sem scroll
    }
    
    // Para outros casos, usa o cálculo padrão
    const calculatedWidth = Math.max(numPeriods * MIN_PERIOD_WIDTH, 2400);
    console.log('📊 MODO PADRÃO - Timeline Width:', calculatedWidth);
    return calculatedWidth;
  }, [expandedPeriodLabels.length, scale, periodLength, availableWidth, getMinColumnWidth]);
  
  // Função para converter posição de scroll em mudança temporal
  const handleTemporalScroll = useCallback((scrollLeft, scrollWidth, clientWidth) => {
    // FUNÇÃO DESABILITADA PARA EVITAR LOOP NO BASEDATE
    console.log('⚠️ handleTemporalScroll DESABILITADO - função não executada');
    return;
    
    // Não processa navegação temporal por scroll durante navegação manual
    if (isManualNavigation) {
      console.log('🚫 Scroll temporal bloqueado - navegação manual ativa');
      return;
    }
    
    console.log('🖱️ SCROLL TEMPORAL DETECTADO');
    console.log('📊 ScrollLeft:', scrollLeft, 'ScrollWidth:', scrollWidth, 'ClientWidth:', clientWidth);
    
    const scrollPercent = scrollLeft / (scrollWidth - clientWidth);
    console.log('📈 ScrollPercent:', scrollPercent);
    
    const bufferPeriods = TEMPORAL_SCROLL_CONFIG.PERIODS_BUFFER;
    
    // Calcula qual período deveria estar no centro da visualização
    const totalExpandedPeriods = expandedPeriodLabels.length;
    const visiblePeriods = periodLength;
    
    console.log('📅 TotalExpandedPeriods:', totalExpandedPeriods, 'VisiblePeriods:', visiblePeriods);
    
    // Posição central da visualização na timeline expandida
    const centerPeriodIndex = Math.round(scrollPercent * (totalExpandedPeriods - visiblePeriods)) + Math.floor(visiblePeriods / 2);
    console.log('🎯 CenterPeriodIndex:', centerPeriodIndex);
    
    // Converte o índice de volta para uma data base
    if (centerPeriodIndex >= bufferPeriods && centerPeriodIndex < totalExpandedPeriods - bufferPeriods) {
      const targetPeriod = expandedPeriodLabels[centerPeriodIndex];
      if (targetPeriod && targetPeriod.start) {
        const newBaseDate = new Date(targetPeriod.start);
        console.log('🎯 Nova data sugerida pelo scroll:', newBaseDate.toISOString().split('T')[0]);
        
        // Só atualiza se a diferença for significativa
        const currentTime = baseDate.getTime();
        const newTime = newBaseDate.getTime();
        const timeDiff = Math.abs(newTime - currentTime);
        
        console.log('⏰ Data atual:', baseDate.toISOString().split('T')[0]);
        console.log('⏰ TimeDiff:', timeDiff);
        
        // Limite mínimo baseado na escala
        let minTimeDiff;
        if (scale === 'month') minTimeDiff = 7 * 24 * 60 * 60 * 1000; // 1 semana
        else if (scale === 'quarter') minTimeDiff = 30 * 24 * 60 * 60 * 1000; // 1 mês
        else if (scale === 'semester') minTimeDiff = 60 * 24 * 60 * 60 * 1000; // 2 meses
        else minTimeDiff = 180 * 24 * 60 * 60 * 1000; // 6 meses
        
        console.log('⏰ MinTimeDiff necessário:', minTimeDiff);
        
        if (timeDiff > minTimeDiff) {
          console.log('✅ APLICANDO mudança de data via scroll temporal');
          setBaseDate(newBaseDate);
        } else {
          console.log('❌ Diferença de tempo insuficiente - scroll ignorado');
        }
      } else {
        console.log('❌ TargetPeriod inválido');
      }
    } else {
      console.log('❌ CenterPeriodIndex fora dos limites do buffer');
    }
  }, [expandedPeriodLabels, periodLength, scale, isManualNavigation]);

  // Effect para rastrear scroll horizontal com navegação temporal
  useEffect(() => {
    console.log('🔄 Configurando listener de scroll...');
    let scrollTimeout;
    let lastScrollLeft = 0;
    
    const handleScroll = () => {
      if (scrollRef.current) {
        const scrollLeft = scrollRef.current.scrollLeft;
        const scrollWidth = scrollRef.current.scrollWidth;
        const clientWidth = scrollRef.current.clientWidth;
        
        console.log('🖱️ SCROLL DETECTADO:', { scrollLeft, scrollWidth, clientWidth });
        
        setScrollState(prev => ({
          ...prev,
          scrollLeft,
          isScrolling: true,
          containerOffsetLeft: 0 // Simplificado - sem offset necessário
        }));
        
        // Atualiza estado da timeline para navegação temporal
        setTimelineScrollState({
          totalWidth: scrollWidth,
          visibleWidth: clientWidth,
          scrollPercent: scrollLeft / (scrollWidth - clientWidth)
        });
        
        // Navegação temporal só acontece em mudanças significativas
        const scrollDelta = Math.abs(scrollLeft - lastScrollLeft);
        if (scrollDelta > TEMPORAL_SCROLL_CONFIG.MIN_SCROLL_DELTA) {
          // TEMPORARIAMENTE DESABILITADO - estava causando loop no baseDate
          // handleTemporalScroll(scrollLeft, scrollWidth, clientWidth);
          console.log('⚠️ handleTemporalScroll DESABILITADO para evitar loop');
          lastScrollLeft = scrollLeft;
        }
        
        // Debounce para resetar o estado de scrolling
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          setScrollState(prev => ({
            ...prev,
            isScrolling: false
          }));
        }, 150);
      }
    };
    
    if (scrollRef.current) {
      console.log('✅ Adicionando listener de scroll ao elemento:', scrollRef.current);
      scrollRef.current.addEventListener('scroll', handleScroll, { passive: true });
      
      return () => {
        console.log('🧹 Removendo listener de scroll');
        if (scrollRef.current) {
          scrollRef.current.removeEventListener('scroll', handleScroll);
        }
        clearTimeout(scrollTimeout);
      };
    } else {
      console.log('❌ scrollRef.current não existe ainda');
    }
  }, [scrollRef.current]); // Dependência no ref atual
  const chartContainerRef = useRef(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: null });
  const [selectedStatuses, setSelectedStatuses] = useState(new Set());
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [viewMode, setViewMode] = useState('gantt'); // 'gantt', 'kanban', 'list'
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  
  // Estados para personalização de cores
  const [customStatusColors, setCustomStatusColors] = useState({});
  const [showColorCustomizer, setShowColorCustomizer] = useState(false);
  
  // Proteção para evitar modo kanban quando feature estiver desabilitada
  useEffect(() => {
    if (!ENABLE_KANBAN_VIEW && viewMode === 'kanban') {
      setViewMode('gantt');
    }
  }, [viewMode]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [exportType, setExportType] = useState('png'); // 'png' ou 'xlsx'
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  
  // Estado para controlar visibilidade da linha hoje
  const [showTodayLine, setShowTodayLine] = useState(true);
  
  // Estados para expand/collapse de itens
  const [expandedItems, setExpandedItems] = useState(() => {
    try {
      const saved = localStorage.getItem(`roadmap-expanded-${currentRoadmapId}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  
  // Estados para edição de itens
  const [editingItem, setEditingItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isStatusDropdownOpen && !event.target.closest('.status-dropdown-container')) {
        setIsStatusDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isStatusDropdownOpen]);

  // Carregar cores customizadas do localStorage
  useEffect(() => {
    const savedColors = localStorage.getItem('roadmap-status-colors');
    if (savedColors) {
      try {
        setCustomStatusColors(JSON.parse(savedColors));
      } catch (error) {
        console.error('Erro ao carregar cores customizadas:', error);
      }
    }
  }, []);

  // Persistir estado de expansão no localStorage
  useEffect(() => {
    if (currentRoadmapId) {
      localStorage.setItem(
        `roadmap-expanded-${currentRoadmapId}`, 
        JSON.stringify([...expandedItems])
      );
    }
  }, [expandedItems, currentRoadmapId]);

  // Função otimizada para toggle de expansão
  const toggleItemExpansion = useCallback((itemId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Opções de períodos disponíveis para cada escala
  const getZoomOptions = (currentScale) => {
    const options = {
      month: [3, 6, 12],
      quarter: [2, 4, 6, 8, 12], // Começando com 2 trimestres, depois 4, 6, 8, 12
      semester: [2, 4, 6],
      year: [1, 2, 3, 5, 10] // Adicionado 1 ano
    };
    return options[currentScale] || [];
  };

  // Função de exportação de imagem
  const chartAreaRef = useRef(null);

  const handleExport = () => {
    if (exportType === 'png') {
      handleExportPNG();
    } else if (exportType === 'xlsx') {
      handleExportXLSX();
    }
  };

  const handleExportPNG = () => {
    if (!chartAreaRef.current) return;
    
    // Track PNG export
    if (plausible) {
      plausible.trackExport('png', 'roadmap', 'roadmap-chart');
      plausible.trackSection('export-png');
    }
    
    // Define que estamos exportando para ocultar os ícones
    setIsExporting(true);
    
    // Aguarda um pequeno delay para garantir que o estado seja aplicado
    setTimeout(() => {
      toPng(chartAreaRef.current, { 
        backgroundColor: '#fff',
        style: {
          overflow: 'visible'
        }
      })
        .then((dataUrl) => {
          saveAs(dataUrl, 'roadmap-chart.png');
        })
        .catch(() => {
          alert('Erro ao exportar imagem');
        })
        .finally(() => {
          // Restaura o estado normal após a exportação
          setIsExporting(false);
        });
    }, 100);
  };

  const handleExportXLSX = async () => {
    try {
      // Track XLSX export
      if (plausible) {
        plausible.trackExport('xlsx', 'roadmap', 'roadmap-chart');
        plausible.trackSection('export-xlsx');
      }
      
      // Função auxiliar para formatar datas como texto puro DD/MM/YYYY
      const formatDateAsText = (dateValue) => {
        if (!dateValue) return '';
        
        // Se já é uma string no formato DD/MM/YYYY, retorna como está
        if (typeof dateValue === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
          return dateValue;
        }
        
        let date;
        if (dateValue instanceof Date) {
          date = dateValue;
        } else if (typeof dateValue === 'string') {
          // Tenta diferentes formatos de data
          if (dateValue.includes('/')) {
            const parts = dateValue.split('/');
            if (parts.length === 3) {
              // Assume DD/MM/YYYY
              date = new Date(parts[2], parts[1] - 1, parts[0]);
            }
          } else if (dateValue.includes('-')) {
            date = new Date(dateValue);
          } else {
            date = new Date(dateValue);
          }
        } else if (typeof dateValue === 'number') {
          // Trata como serial Excel (dias desde 1899-12-30)
          const excelEpoch = new Date(1899, 11, 30);
          date = new Date(excelEpoch.getTime() + dateValue * 24 * 60 * 60 * 1000);
        } else {
          return String(dateValue);
        }
        
        // Verifica se a data é válida
        if (!date || isNaN(date.getTime())) {
          return String(dateValue);
        }
        
        // Formata como DD/MM/YYYY (texto puro, sem apóstrofe)
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };

      // Criar workbook e worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Roadmap');
      
      // Configurar propriedades do workbook
      workbook.creator = 'Dash Simples';
      workbook.lastModifiedBy = 'Dash Simples';
      workbook.created = new Date();
      workbook.modified = new Date();
      
      // Configurar colunas com larguras
      worksheet.columns = [
        { header: 'Nome', key: 'nome', width: 30 },
        { header: 'Nível', key: 'nivel', width: 12 },
        { header: 'Status', key: 'status', width: 15 },
        { header: 'Data de Inicio', key: 'dataInicio', width: 18 },
        { header: 'Previsão de Entrega', key: 'previsaoEntrega', width: 18 },
        { header: 'Data de Conclusão', key: 'dataConclusao', width: 18 }
      ];
      
      // Adicionar dados linha por linha mantendo a ordem hierárquica
      flattenedData.forEach(item => {
        // Determinar nível baseado no level interno
        const nivel = item.level === 1 ? 'Subitem' : 'Item';
        
        const row = worksheet.addRow({
          nome: item.Nome || item.nome || '',
          nivel: nivel,
          status: item.Status || item.status || '',
          dataInicio: formatDateAsText(item['Data de Inicio'] || item.dataInicio),
          previsaoEntrega: formatDateAsText(item['Previsão de Entrega'] || item.previsaoEntrega),
          dataConclusao: formatDateAsText(item['Data de Conclusão'] || item.dataConclusao)
        });
        
        // Forçar colunas de data como texto
        row.getCell('dataInicio').numFmt = '@';
        row.getCell('previsaoEntrega').numFmt = '@';
        row.getCell('dataConclusao').numFmt = '@';
      });
      
      // Estilizar cabeçalho
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      };
      
      // Gerar buffer e fazer download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const filename = `roadmap-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      saveAs(blob, filename);
      
    } catch (error) {
      console.error('Erro na exportação XLSX:', error);
      alert('Erro ao exportar para XLSX: ' + error.message);
    }
  };

  const handleExportClick = () => {
    setExportType('png'); // Reset para PNG por padrão
    setShowConfirmModal(true);
  };

  const confirmExport = () => {
    setShowConfirmModal(false);
    handleExport();
  };

  const cancelExport = () => {
    setShowConfirmModal(false);
    setExportType('png'); // Reset para PNG
  };

  // Funções para modal de confirmação de remoção
  const handleRemoveClick = (item) => {
    setItemToRemove(item);
    setShowRemoveConfirmModal(true);
  };

  const confirmRemove = () => {
    if (itemToRemove && onRemoveItem) {
      onRemoveItem(itemToRemove.id);
    }
    setShowRemoveConfirmModal(false);
    setItemToRemove(null);
  };

  const cancelRemove = () => {
    setShowRemoveConfirmModal(false);
    setItemToRemove(null);
  };

  // Funções para gerenciar cores customizadas
  const saveCustomColors = (colors) => {
    setCustomStatusColors(colors);
    localStorage.setItem('roadmap-status-colors', JSON.stringify(colors));
  };

  const updateStatusColor = (status, color) => {
    const newColors = { ...customStatusColors, [status]: color };
    saveCustomColors(newColors);
  };

  const resetStatusColors = () => {
    setCustomStatusColors({});
    localStorage.removeItem('roadmap-status-colors');
  };

  const getCurrentStatusColor = (status) => {
    // 1ª prioridade: Cores customizadas
    if (customStatusColors[status]) {
      return customStatusColors[status];
    }
    // 2ª prioridade: Cores padrão
    if (ROADMAP_STATUS_COLORS[status]) {
      return ROADMAP_STATUS_COLORS[status];
    }
    // 3ª prioridade: Cor padrão
    return '#64748b';
  };

  // Extrai todos os status únicos dos dados
  const availableStatuses = useMemo(() => {
    const statuses = [...new Set(data.map(item => item['Status']).filter(status => status && status !== '-'))];
    return statuses.sort();
  }, [data]);

  // Inicializa com todos os status selecionados quando os dados mudam
  useEffect(() => {
    if (availableStatuses.length > 0) {
      setSelectedStatuses(new Set(availableStatuses));
    }
  }, [availableStatuses]);

  // Função para verificar se uma data está dentro do período selecionado
  const isDateInSelectedPeriod = (date) => {
    if (!date || selectedPeriod === 'all') return true;
    
    const today = new Date();
    const currentMonth = today.getUTCMonth();
    const currentYear = today.getUTCFullYear();
    
    switch (selectedPeriod) {
      case 'thisMonth':
        return date.getUTCMonth() === currentMonth && date.getUTCFullYear() === currentYear;
      
      case 'nextMonth':
        const nextMonth = new Date(Date.UTC(currentYear, currentMonth + 1, 1));
        return date.getUTCMonth() === nextMonth.getUTCMonth() && date.getUTCFullYear() === nextMonth.getUTCFullYear();
      
      case 'last3Months':
        const threeMonthsAgo = new Date(Date.UTC(currentYear, currentMonth - 3, 1));
        return date >= threeMonthsAgo && date <= today;
      
      case 'next3Months':
        const threeMonthsFromNow = new Date(Date.UTC(currentYear, currentMonth + 3, 31));
        return date >= today && date <= threeMonthsFromNow;
      
      case 'thisYear':
        return date.getUTCFullYear() === currentYear;
      
      case 'custom':
        if (!customStartDate && !customEndDate) return true;
        const startDate = customStartDate ? new Date(customStartDate + 'T12:00:00Z') : null;
        const endDate = customEndDate ? new Date(customEndDate + 'T12:00:00Z') : null;
        
        if (startDate && endDate) {
          return date >= startDate && date <= endDate;
        } else if (startDate) {
          return date >= startDate;
        } else if (endDate) {
          return date <= endDate;
        }
        return true;
      
      default:
        return true;
    }
  };

  // Filtra dados baseado nos status e período selecionados
  const filteredData = useMemo(() => {
    if (selectedStatuses.size === 0) return [];
    
    return data.filter(item => {
      // Filtro por status - inclui itens com status "-" sempre
      const statusMatch = selectedStatuses.has(item['Status']) || item['Status'] === '-';
      
      // Filtro por período (baseado na Data de Início)
      const startDate = parseDate(item['Data de Inicio']);
      const periodMatch = isDateInSelectedPeriod(startDate);
      
      return statusMatch && periodMatch;
    });
  }, [data, selectedStatuses, selectedPeriod, customStartDate, customEndDate]);

  // Organiza dados em estrutura hierárquica
  const hierarchicalData = useMemo(() => {
    console.log('🔍 DEBUG hierarchicalData - filteredData:', filteredData);
    
    // ABORDAGEM 1: Verificar se já existe propriedade 'subitens' nos dados
    const itemsWithSubitens = filteredData.filter(item => item.subitens && Array.isArray(item.subitens) && item.subitens.length > 0);
    console.log('🔍 DEBUG - itens com propriedade subitens:', itemsWithSubitens);
    
    if (itemsWithSubitens.length > 0) {
      // Se já existe a propriedade subitens, usa ela
      console.log('✅ Usando estrutura hierárquica existente (propriedade subitens)');
      return filteredData.filter(item => 
        // Inclui apenas itens que não têm parentId (itens principais)
        !item.parentId && (item.level === 0 || item.level === undefined)
      );
    }
    
    // ABORDAGEM 2: Usar level e parentId
    const mainItems = filteredData.filter(item => 
      (item.level === 0 || item.level === undefined) && !item.parentId
    );
    
    const subItems = filteredData.filter(item => 
      item.level === 1 && item.parentId
    );
    
    console.log('🔍 DEBUG - mainItems:', mainItems.length, mainItems);
    console.log('🔍 DEBUG - subItems:', subItems.length, subItems);
    
    if (subItems.length > 0) {
      // Se temos subitens definidos por level/parentId
      console.log('✅ Usando estrutura hierárquica baseada em level/parentId');
      
      // Cria a estrutura hierárquica
      const organized = mainItems.map(item => ({ ...item, subitems: [] }));
      
      // Associa subitens aos seus pais
      subItems.forEach(subitem => {
        const parentIndex = organized.findIndex(parent => {
          return String(parent.id) === String(subitem.parentId);
        });
        
        if (parentIndex !== -1) {
          organized[parentIndex].subitems.push(subitem);
          console.log(`✅ Subitem ${subitem.Nome || subitem.nome} associado ao pai ${organized[parentIndex].Nome || organized[parentIndex].nome}`);
        } else {
          console.log(`❌ Pai não encontrado para subitem ${subitem.Nome || subitem.nome} (parentId: ${subitem.parentId})`);
        }
      });
      
      console.log('🔍 DEBUG - organized final:', organized);
      console.log('🔍 DEBUG - itens com subitens:', organized.filter(item => item.subitems && item.subitems.length > 0));
      
      return organized;
    }
    
    // ABORDAGEM 3: Fallback - retorna todos os itens como principais (sem hierarquia)
    console.log('⚠️ Nenhuma estrutura hierárquica detectada, tratando todos como itens principais');
    return filteredData.map(item => ({ ...item, subitems: [] }));
  }, [filteredData]);

  // Auto-expandir todos os itens com subitens por padrão
  useEffect(() => {
    const itemsWithSubitems = hierarchicalData
      .filter(item => item.subitems && item.subitems.length > 0)
      .map(item => item.id);
    
    if (itemsWithSubitems.length > 0) {
      setExpandedItems(prev => {
        const newExpanded = new Set(prev);
        itemsWithSubitems.forEach(id => newExpanded.add(id));
        return newExpanded;
      });
    }
  }, [hierarchicalData]);

  // Funções para expandir/colapsar todos (definidas após hierarchicalData)
  const expandAllItems = useCallback(() => {
    const allParentIds = hierarchicalData
      .filter(item => item.subitems && item.subitems.length > 0)
      .map(item => item.id);
    setExpandedItems(new Set(allParentIds));
  }, [hierarchicalData]);

  const collapseAllItems = useCallback(() => {
    setExpandedItems(new Set());
  }, []);

  // Função para achatar a hierarquia para visualização no Gantt (considera estado de expansão)
  const flattenedData = useMemo(() => {
    const flattened = [];
    hierarchicalData.forEach(item => {
      const hasChildren = item.subitems && item.subitems.length > 0;
      const isExpanded = expandedItems.has(item.id);
      
      flattened.push({ 
        ...item, 
        isParent: true, 
        hasChildren, 
        isExpanded 
      });
      
      // Só inclui subitens se o item pai estiver expandido
      if (isExpanded && hasChildren) {
        item.subitems.forEach(subitem => {
          flattened.push({ 
            ...subitem, 
            isSubitem: true, 
            parentName: item.Nome || item.nome 
          });
        });
      }
    });
    
    return flattened;
  }, [hierarchicalData, expandedItems]);

  // Função para toggle de status
  const toggleStatus = (status) => {
    const newSelectedStatuses = new Set(selectedStatuses);
    if (newSelectedStatuses.has(status)) {
      newSelectedStatuses.delete(status);
    } else {
      newSelectedStatuses.add(status);
    }
    setSelectedStatuses(newSelectedStatuses);
  };

  // Função para selecionar/deselecionar todos
  const toggleAll = () => {
    if (selectedStatuses.size === availableStatuses.length) {
      setSelectedStatuses(new Set());
    } else {
      setSelectedStatuses(new Set(availableStatuses));
    }
  };

  // Lógica para scroll horizontal por arraste
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    function handleMouseDown(e) {
      isDown = true;
      el.classList.add('cursor-grabbing');
      startX = e.pageX - el.getBoundingClientRect().left;
      scrollLeft = el.scrollLeft;
      // Evita seleção de texto
      document.body.style.userSelect = 'none';
    }
    function handleMouseLeave() {
      isDown = false;
      el.classList.remove('cursor-grabbing');
      document.body.style.userSelect = '';
    }
    function handleMouseUp() {
      isDown = false;
      el.classList.remove('cursor-grabbing');
      document.body.style.userSelect = '';
    }
    function handleMouseMove(e) {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.getBoundingClientRect().left;
      const walk = (x - startX) * 1.2; // sensibilidade
      el.scrollLeft = scrollLeft - walk;
    }
    el.addEventListener('mousedown', handleMouseDown);
    el.addEventListener('mouseleave', handleMouseLeave);
    el.addEventListener('mouseup', handleMouseUp);
    el.addEventListener('mousemove', handleMouseMove);
    return () => {
      el.removeEventListener('mousedown', handleMouseDown);
      el.removeEventListener('mouseleave', handleMouseLeave);
      el.removeEventListener('mouseup', handleMouseUp);
      el.removeEventListener('mousemove', handleMouseMove);
      document.body.style.userSelect = '';
    };
  }, []);

  // Processa datas e encontra o range do Gantt
  const items = useMemo(() => {
    return flattenedData.map(item => {
      const start = parseDate(item['Data de Inicio']);
      const dataConclusao = parseDate(item['Data de Conclusão']);
      const previsaoEntrega = parseDate(item['Previsão de Entrega']);
      
      // Usa Data de Conclusão se existir, senão Previsão de Entrega para definir o fim da barra
      const end = dataConclusao || previsaoEntrega;
      
      // Detecta se há atraso (conclusão após previsão)
      const hasDelay = dataConclusao && previsaoEntrega && dataConclusao > previsaoEntrega;
      
      return {
        nome: item['Nome'] || item.Nome, // Suporte para ambos os formatos
        status: item['Status'] || item.Status,
        start,
        end,
        dataConclusao,
        previsaoEntrega,
        hasValidDates: start && end, // indica se tem datas válidas para mostrar barra
        hasDelay, // indica se há atraso
        dataConclusaoOriginal: item['Data de Conclusão'], // mantém valor original para tooltip
        previsaoEntregaOriginal: item['Previsão de Entrega'], // mantém valor original para tooltip
        isParent: item.isParent,
        isSubitem: item.isSubitem,
        parentName: item.parentName,
        level: item.level || 0,
        id: item.id,
        parentId: item.parentId,
        hasChildren: item.hasChildren, // CORREÇÃO: Incluir propriedade hasChildren
        isExpanded: item.isExpanded    // CORREÇÃO: Incluir propriedade isExpanded
      };
    });
  }, [flattenedData]);

  // Calendário contínuo baseado na escala e período selecionados
  const [minDate, maxDate] = useMemo(() => {
    let startDate, endDate;
    
    if (scale === 'month') {
      // Começa no primeiro dia do mês base
      startDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
      // Termina no último dia do mês após periodLength meses
      endDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + periodLength, 0, 23, 59, 59);
    } else if (scale === 'quarter') {
      // Começa no primeiro dia do trimestre base
      const quarterStart = Math.floor(baseDate.getMonth() / 3) * 3;
      startDate = new Date(baseDate.getFullYear(), quarterStart, 1);
      // Termina no último dia do trimestre após periodLength trimestres
      const endYear = baseDate.getFullYear() + Math.floor((quarterStart + (periodLength * 3)) / 12);
      const endMonth = (quarterStart + (periodLength * 3)) % 12;
      endDate = new Date(endYear, endMonth, 0, 23, 59, 59);
    } else if (scale === 'semester') {
      // Começa no primeiro dia do semestre base
      const semesterStart = baseDate.getMonth() < 6 ? 0 : 6;
      startDate = new Date(baseDate.getFullYear(), semesterStart, 1);
      // Termina no último dia do semestre após periodLength semestres
      const endYear = baseDate.getFullYear() + Math.floor((semesterStart + (periodLength * 6)) / 12);
      const endMonth = (semesterStart + (periodLength * 6)) % 12;
      endDate = new Date(endYear, endMonth, 0, 23, 59, 59);
    } else if (scale === 'year') {
      // Começa no primeiro dia do ano base
      startDate = new Date(baseDate.getFullYear(), 0, 1);
      // Termina no último dia do ano após periodLength anos
      endDate = new Date(baseDate.getFullYear() + periodLength, 0, 0, 23, 59, 59);
    }
    
    // Expande o range APENAS para incluir datas das barras modificadas que estão próximas ao período atual
    // Não força expansão se os dados estão muito longe do período de navegação
    if (Object.keys(modifiedData).length > 0) {
      Object.values(modifiedData).forEach(modifications => {
        if (modifications.start) {
          const modifiedStart = new Date(modifications.start);
          // Só expande o início se a data modificada estiver dentro de uma margem razoável
          const monthsDiff = (startDate.getFullYear() - modifiedStart.getFullYear()) * 12 + 
                            (startDate.getMonth() - modifiedStart.getMonth());
          
          // Permite expansão máxima de 6 meses para trás
          if (modifiedStart < startDate && monthsDiff <= 6) {
            if (scale === 'month') {
              startDate = new Date(modifiedStart.getFullYear(), modifiedStart.getMonth(), 1);
            } else if (scale === 'quarter') {
              const quarterStart = Math.floor(modifiedStart.getMonth() / 3) * 3;
              startDate = new Date(modifiedStart.getFullYear(), quarterStart, 1);
            } else if (scale === 'semester') {
              const semesterStart = modifiedStart.getMonth() < 6 ? 0 : 6;
              startDate = new Date(modifiedStart.getFullYear(), semesterStart, 1);
            } else if (scale === 'year') {
              startDate = new Date(modifiedStart.getFullYear(), 0, 1);
            }
          }
        }
        
        const endDates = [modifications.end, modifications.dataConclusao].filter(Boolean);
        endDates.forEach(endDateToCheck => {
          if (endDateToCheck && endDateToCheck > endDate) {
            // Sempre permite expansão para o futuro
            const modifiedEnd = new Date(endDateToCheck);
            if (scale === 'month') {
              endDate = new Date(modifiedEnd.getFullYear(), modifiedEnd.getMonth() + 1, 0, 23, 59, 59);
            } else if (scale === 'quarter') {
              const quarterEnd = Math.floor(modifiedEnd.getMonth() / 3) * 3 + 3;
              const endYear = modifiedEnd.getFullYear() + Math.floor(quarterEnd / 12);
              const endMonth = quarterEnd % 12;
              endDate = new Date(endYear, endMonth, 0, 23, 59, 59);
            } else if (scale === 'semester') {
              const semesterEnd = modifiedEnd.getMonth() < 6 ? 6 : 12;
              const endYear = modifiedEnd.getFullYear() + (semesterEnd === 12 ? 1 : 0);
              const endMonth = semesterEnd % 12;
              endDate = new Date(endYear, endMonth, 0, 23, 59, 59);
            } else if (scale === 'year') {
              endDate = new Date(modifiedEnd.getFullYear() + 1, 0, 0, 23, 59, 59);
            }
          }
        });
      });
    }
    
    return [startDate, endDate];
  }, [scale, baseDate, periodLength, modifiedData]);

  // Gera os labels do eixo X conforme a escala
  const periodLabels = useMemo(() => {
    if (!minDate || !maxDate) return [];
    const labels = getHeaderLabels(minDate, maxDate, scale);
    let intervals = [];
    let current = new Date(Date.UTC(minDate.getUTCFullYear(), minDate.getUTCMonth(), 1));
    while (true) {
      let start = new Date(current.getTime());
      let end;
      if (scale === 'month') {
        // Último dia do mês corrente
        end = new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + 1, 0, 23, 59, 59));
        intervals.push({ start, end });
        // Avança para o próximo mês
        current.setUTCMonth(current.getUTCMonth() + 1);
        // Se o próximo mês começa depois do maxDate, pare
        if (current > maxDate) break;
      } else if (scale === 'quarter') {
        const nextQuarter = current.getUTCMonth() + 3 - (current.getUTCMonth() % 3);
        end = new Date(Date.UTC(current.getUTCFullYear(), nextQuarter, 0, 23, 59, 59));
        current.setUTCMonth(nextQuarter);
      } else if (scale === 'semester') {
        if (current.getUTCMonth() < 6) {
          end = new Date(Date.UTC(current.getUTCFullYear(), 6, 0, 23, 59, 59));
          current.setUTCMonth(6);
        } else {
          end = new Date(Date.UTC(current.getUTCFullYear(), 12, 0, 23, 59, 59));
          current.setUTCFullYear(current.getUTCFullYear() + 1);
          current.setUTCMonth(0);
        }
      } else if (scale === 'year') {
        end = new Date(Date.UTC(current.getUTCFullYear() + 1, 0, 0, 23, 59, 59));
        current.setUTCFullYear(current.getUTCFullYear() + 1);
      }
      intervals.push({ start, end });
      if (current > maxDate) break;
    }
    return labels.map((label, i) => ({ label, ...intervals[i] }));
  }, [minDate, maxDate, scale, baseDate]);

  // Calcular largura total do timeline para permitir scroll (usa timeline expandida)
  const timelineWidth = useMemo(() => {
    return expandedTimelineWidth;
  }, [expandedTimelineWidth]);

  // Debug para scroll
  useEffect(() => {
    // Logs de debug removidos para focar no drag
  }, [timelineWidth, expandedPeriodLabels.length]);

  // Estados para drag horizontal
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartScrollLeft, setDragStartScrollLeft] = useState(0);

  // Funções de drag horizontal
  const handleMouseDown = useCallback((e) => {
    if (scrollRef.current) {
      setIsDragging(true);
      setDragStartX(e.clientX);
      setDragStartScrollLeft(scrollRef.current.scrollLeft);
      console.log('🖱️ Drag iniciado:', { startX: e.clientX, startScrollLeft: scrollRef.current.scrollLeft });
      
      // Previne seleção de texto durante o drag
      e.preventDefault();
      document.body.style.userSelect = 'none';
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !scrollRef.current) return;
    
    const deltaX = e.clientX - dragStartX;
    const newScrollLeft = dragStartScrollLeft - deltaX; // Inverte para feel natural
    
    // Calcula limites
    const minScrollLeft = 0;
    const maxScrollLeft = scrollRef.current.scrollWidth - scrollRef.current.clientWidth;
    
    // Se tentar ir além dos limites, navega temporalmente
    const DRAG_THRESHOLD = 50; // px necessários para navegar
    
    if (newScrollLeft < 0 && Math.abs(newScrollLeft) > DRAG_THRESHOLD) {
      // Drag para trás além do limite - navegar para período anterior
      console.log('🔙 Navegando para período anterior via drag');
      navigatePrevious();
      
      // Redefine posição de drag para continuar smooth
      setDragStartX(e.clientX);
      setDragStartScrollLeft(scrollRef.current.scrollLeft);
      return;
    }
    
    if (newScrollLeft > maxScrollLeft && (newScrollLeft - maxScrollLeft) > DRAG_THRESHOLD) {
      // Drag para frente além do limite - navegar para próximo período  
      console.log('▶️ Navegando para próximo período via drag');
      navigateNext();
      
      // Redefine posição de drag para continuar smooth
      setDragStartX(e.clientX);
      setDragStartScrollLeft(scrollRef.current.scrollLeft);
      return;
    }
    
    // Scroll normal dentro dos limites
    const clampedScrollLeft = Math.max(minScrollLeft, Math.min(newScrollLeft, maxScrollLeft));
    scrollRef.current.scrollLeft = clampedScrollLeft;
    
    console.log('🖱️ Dragging:', { 
      deltaX, 
      newScrollLeft,
      clampedScrollLeft,
      direction: deltaX > 0 ? 'direita (passado)' : 'esquerda (futuro)',
      beyondLimits: newScrollLeft < 0 || newScrollLeft > maxScrollLeft
    });
  }, [isDragging, dragStartX, dragStartScrollLeft, navigatePrevious, navigateNext]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      document.body.style.userSelect = '';
      console.log('🖱️ Drag finalizado');
    }
  }, [isDragging]);

  // Effect para gerenciar eventos globais de mouse
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('mouseleave', handleMouseUp); // Para quando o mouse sai da janela
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('mouseleave', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Debug adicional após montagem
  useEffect(() => {
    const checkScroll = () => {
      if (scrollRef.current) {
        const element = scrollRef.current;
        console.log('✅ Scroll Element Montado:');
        console.log('  - clientWidth:', element.clientWidth);
        console.log('  - scrollWidth:', element.scrollWidth);
        console.log('  - hasHorizontalScroll:', element.scrollWidth > element.clientWidth);
        console.log('  - overflowX:', window.getComputedStyle(element).overflowX);
        console.log('  - elemento completo:', element);
        
        // Adiciona listener para testar scroll
        const handleScroll = () => {
          console.log('🖱️ SCROLL DETECTADO! scrollLeft:', element.scrollLeft);
        };
        
        element.addEventListener('scroll', handleScroll);
        
        return () => {
          element.removeEventListener('scroll', handleScroll);
        };
      }
    };

    // Verifica imediatamente e também após um pequeno delay
    checkScroll();
    const timeout = setTimeout(checkScroll, 100);
    
    return () => clearTimeout(timeout);
  }, []); // Executa apenas uma vez após montagem

  // ========================================
  // FUNÇÕES ANTIGAS (BACKUP) - NÃO USAR
  // ========================================
  /*
  // Calcula a posição e tamanho das barras em porcentagem
  function getBarStyle(start, end) {
    if (!minDate || !maxDate || !periodLabels.length || !periodLabels[0] || !periodLabels[periodLabels.length - 1]) return {};
    if (!start || !end) return {};
    
    // Use periodLabels para cálculo - eles representam o que está visível na timeline
    const first = periodLabels[0];
    const last = periodLabels[periodLabels.length - 1];
    if (!first.start || !last.end) return {};
    
    const total = last.end.getTime() - first.start.getTime();
    if (total <= 0) return {};

    // Verifica se a barra tem alguma interseção com a timeline visível
    if (end.getTime() < first.start.getTime() || start.getTime() > last.end.getTime()) {
      // Barra completamente fora da timeline visível
      return { display: 'none' };
    }

    // Clipa as datas para ficarem dentro da timeline visível
    const clippedStart = new Date(Math.max(start.getTime(), first.start.getTime()));
    const clippedEnd = new Date(Math.min(end.getTime(), last.end.getTime()));

    // Calcula a posição e largura baseada no range visível e clippado
    const left = ((clippedStart.getTime() - first.start.getTime()) / total) * 100;
    const width = ((clippedEnd.getTime() - clippedStart.getTime()) / total) * 100;
    
    // Debug temporário para entender o problema de navegação temporal
    const debugKey = `${start.toISOString().split('T')[0]}-${first.start.toISOString().split('T')[0]}`;
    if (!window.debuggedKeys) window.debuggedKeys = new Set();
    
    if (start.toISOString().includes('2025-07') && !window.debuggedKeys.has(debugKey)) {
      window.debuggedKeys.add(debugKey);
      console.log('� Timeline Position DEBUG:', {
        item: `${start.toISOString().split('T')[0]} to ${end.toISOString().split('T')[0]}`,
        timeline: `${first.start.toISOString().split('T')[0]} to ${last.end.toISOString().split('T')[0]}`,
        clipped: `${clippedStart.toISOString().split('T')[0]} to ${clippedEnd.toISOString().split('T')[0]}`,
        position: `left:${left.toFixed(1)}% width:${width.toFixed(1)}%`
      });
    }
    
    return {
      left: `${left}%`,
      width: `${width}%`,
    };
  }

  // Calcula estilo da barra de atraso (parte vermelha)
  function getDelayBarStyle(previsaoEntrega, dataConclusao) {
    if (!minDate || !maxDate || !previsaoEntrega || !dataConclusao || !periodLabels.length || !periodLabels[0] || !periodLabels[periodLabels.length - 1]) return {};
    
    // Use periodLabels para cálculo - eles representam o que está visível na timeline
    const first = periodLabels[0];
    const last = periodLabels[periodLabels.length - 1];
    if (!first.start || !last.end) return {};
    
    const total = last.end.getTime() - first.start.getTime();
    if (total <= 0) return {};
    
    const left = ((previsaoEntrega.getTime() - first.start.getTime()) / total) * 100;
    const width = ((dataConclusao.getTime() - previsaoEntrega.getTime()) / total) * 100;
    
    return {
      left: `${left}%`,
      width: `${width}%`,
    };
  }
  */

  // ========================================
  // SISTEMA DE COORDENADAS ABSOLUTAS - SOLUÇÃO DEFINITIVA
  // ========================================
  
  // NOVA LÓGICA: Cálculo simples e direto de posicionamento de barras usando timeline expandida
  const calculateSimpleBarPosition = useCallback((startDate, endDate) => {
    // CORREÇÃO: Usar os mesmos períodos que as colunas usam (visiblePeriodLabels)
    const { visibleStart, visibleEnd } = visiblePeriodLabels;
    
    // DEBUG: Log específico para análise do problema
    if (startDate && startDate.getMonth() === 5 && startDate.getFullYear() === 2025) { // Junho 2025
      console.log('🔍 DEBUG ATIVIDADE 1 - calculateSimpleBarPosition:');
      console.log('📅 startDate:', startDate.toISOString());
      console.log('📅 endDate:', endDate?.toISOString());
      console.log('📅 visibleStart:', visibleStart?.toISOString());
      console.log('📅 visibleEnd:', visibleEnd?.toISOString());
      
      // DEBUG: Verificar exatamente onde está o período de junho
      const juneStart = new Date(Date.UTC(2025, 5, 1, 0, 0, 0)); // 01 de junho
      const mayEnd = new Date(Date.UTC(2025, 4, 31, 23, 59, 59)); // 31 de maio
      console.log('📅 Junho deveria começar em:', juneStart.toISOString());
      console.log('📅 Maio deveria terminar em:', mayEnd.toISOString());
      console.log('📅 Diferença entre maio e junho (ms):', juneStart.getTime() - mayEnd.getTime());
    }
    
    // Verificações de segurança mais rigorosas
    if (!visibleStart || !visibleEnd || !startDate || !endDate) {
      return { display: 'none', left: '0%', width: '0%' };
    }

    // Verificação se as datas são válidas
    if (!(visibleStart instanceof Date) || !(visibleEnd instanceof Date) || 
        !(startDate instanceof Date) || !(endDate instanceof Date) ||
        isNaN(visibleStart.getTime()) || isNaN(visibleEnd.getTime()) ||
        isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return { display: 'none', left: '0%', width: '0%' };
    }

    // Converte datas para timestamps usando os períodos visíveis (mesmo que as colunas)
    const timelineStart = visibleStart.getTime();
    const timelineEnd = visibleEnd.getTime();
    const timelineDuration = timelineEnd - timelineStart;
    
    // Verificação se a duração da timeline é válida
    if (timelineDuration <= 0) {
      return { display: 'none', left: '0%', width: '0%' };
    }
    
    let barStart = startDate.getTime();
    let barEnd = endDate.getTime();
    
    // CORREÇÃO PARA ALINHAMENTO PRECISO: Se a data de início for muito próxima do início de um mês,
    // força ela para o início exato do mês para evitar vazamentos visuais
    if (scale === 'month') {
      const startMonth = startDate.getUTCMonth();
      const startYear = startDate.getUTCFullYear();
      const startDay = startDate.getUTCDate();
      
      // Se é dia 1, força para o início exato do mês
      if (startDay === 1) {
        const exactMonthStart = new Date(Date.UTC(startYear, startMonth, 1, 0, 0, 0));
        barStart = exactMonthStart.getTime();
        console.log('🔧 CORREÇÃO: Forçando início para exato do mês:', exactMonthStart.toISOString());
      }
    }
    
    // Verifica se a barra está dentro do período visível
    if (barEnd < timelineStart || barStart > timelineEnd) {
      return { display: 'none', left: '0%', width: '0%' };
    }
    
    // Calcula posições como percentual da timeline expandida
    const leftPercent = Math.max(0, ((barStart - timelineStart) / timelineDuration) * 100);
    const rightPercent = Math.min(100, ((barEnd - timelineStart) / timelineDuration) * 100);
    const widthPercent = rightPercent - leftPercent;
    
    // DEBUG: Log dos cálculos para o problema relatado
    if (startDate && startDate.getMonth() === 5 && startDate.getFullYear() === 2025) { // Junho 2025
      console.log('🔍 DEBUG ATIVIDADE 1 - Cálculos:');
      console.log('⏱️ timelineStart:', new Date(timelineStart).toISOString());
      console.log('⏱️ timelineEnd:', new Date(timelineEnd).toISOString());
      console.log('⏱️ timelineDuration (dias):', timelineDuration / (1000 * 60 * 60 * 24));
      console.log('📊 barStart:', new Date(barStart).toISOString());
      console.log('📊 barEnd:', new Date(barEnd).toISOString());
      console.log('📊 leftPercent:', leftPercent.toFixed(2) + '%');
      console.log('📊 widthPercent:', widthPercent.toFixed(2) + '%');
      
      // Calcular onde deveria estar junho exatamente
      const juneStartUTC = new Date(Date.UTC(2025, 5, 1, 0, 0, 0)).getTime();
      const juneLeftPercent = ((juneStartUTC - timelineStart) / timelineDuration) * 100;
      console.log('📊 Junho deveria começar em:', juneLeftPercent.toFixed(2) + '%');
      console.log('📊 Diferença para barra atual:', (leftPercent - juneLeftPercent).toFixed(2) + '%');
    }
    
    if (widthPercent <= 0) {
      return { display: 'none', left: '0%', width: '0%' };
    }
    
    // Verificação final se os valores calculados são números válidos
    if (isNaN(leftPercent) || isNaN(widthPercent) || leftPercent < 0 || widthPercent <= 0) {
      return { display: 'none', left: '0%', width: '0%' };
    }
    
    return {
      left: `${leftPercent}%`,
      width: `${widthPercent}%`,
      position: 'absolute'
    };
  }, [visiblePeriodLabels]);

  // Função para obter estilo da barra principal de um item
  const getItemBarStyle = useCallback((item) => {
    const effectiveData = getEffectiveItemData(item);
    
    // Para itens sem datas válidas
    if (!effectiveData.start || !effectiveData.end) {
      return { display: 'none' };
    }
    
    // Calcula a posição básica
    const baseStyle = calculateSimpleBarPosition(effectiveData.start, effectiveData.end);
    
    // Verificação de segurança: se baseStyle não tem as propriedades esperadas
    if (!baseStyle || !baseStyle.left || !baseStyle.width || baseStyle.display === 'none') {
      return { display: 'none' };
    }
    
    // Verificação adicional se left e width são strings com %
    if (typeof baseStyle.left !== 'string' || typeof baseStyle.width !== 'string' || 
        !baseStyle.left.includes('%') || !baseStyle.width.includes('%')) {
      return { display: 'none' };
    }
    
    // Ajusta a posição left para considerar o scroll
    const leftPercent = parseFloat(baseStyle.left.replace('%', ''));
    const widthPercent = parseFloat(baseStyle.width.replace('%', ''));
    
    // Verificação se os valores são números válidos
    if (isNaN(leftPercent) || isNaN(widthPercent)) {
      return { display: 'none' };
    }
    
    const adjustedLeft = (leftPercent / 100 * expandedTimelineWidth) - scrollState.scrollLeft;
    
    // Log para debug no modo responsivo
    if ((scale === 'month' && periodLength <= 12) || (scale === 'quarter' && periodLength <= 8)) {
      console.log('🎯 BARRA RESPONSIVA:', {
        scale: scale,
        timelineWidth: expandedTimelineWidth,
        leftPercent: leftPercent.toFixed(1),
        widthPercent: widthPercent.toFixed(1),
        adjustedLeft: adjustedLeft.toFixed(1)
      });
    }
    
    return {
      ...baseStyle,
      left: `${adjustedLeft}px`,
      width: `${widthPercent / 100 * expandedTimelineWidth}px`
    };
  }, [calculateSimpleBarPosition, getEffectiveItemData, expandedTimelineWidth, scrollState.scrollLeft]);

  // Função para mostrar tooltip customizado (relativo ao container, com ajuste para não cortar nos limites)
  // Usamos throttle para reduzir atualizações excessivas do tooltip em movimento rápido do mouse
  function handleBarMouseEnter(e, item) {
    // Não processamos se o tooltip já estiver visível para o mesmo item
    if (tooltip.visible && tooltip.content && tooltip.content.nome === item.nome) {
      return;
    }
    
    if (!chartContainerRef.current || !scrollRef.current) return;
    const barRect = e.target.getBoundingClientRect();
    const containerRect = chartContainerRef.current.getBoundingClientRect();
    const scrollLeft = scrollRef.current ? scrollRef.current.scrollLeft : 0;
    
    // Posição central da barra
    const barCenterX = barRect.left + barRect.width / 2 - containerRect.left + scrollLeft;
    const barTop = barRect.top - containerRect.top;
    const barBottom = barRect.bottom - containerRect.top;
    
    // Dimensões do tooltip (atualizadas para valores mais precisos)
    const tooltipHeight = 110; // px (altura máxima com atraso)
    const tooltipWidth = 260; // px (largura máxima do conteúdo)
    
    // Dimensões e limites do container visível
    const containerWidth = scrollRef.current.clientWidth; // Largura visível
    const containerHeight = chartContainerRef.current.clientHeight;
    const visibleLeft = scrollLeft;
    const visibleRight = visibleLeft + containerWidth;
    
    // Espaço disponível em cada direção
    const barX = barCenterX;
    const barY = (barTop + barBottom) / 2;
    const spaceRight = visibleRight - barX;
    const spaceLeft = barX - visibleLeft;
    const spaceAbove = barTop;
    const spaceBelow = containerHeight - barBottom;
    
    // Decisão vertical - acima ou abaixo
    let showAbove = spaceAbove >= tooltipHeight + 10 || spaceAbove > spaceBelow;
    
    // Decisão horizontal - left, center, right
    let align;
    if (spaceRight < tooltipWidth / 2 + 20) {
      // Próximo à borda direita - alinhar à direita
      align = 'right';
    } else if (spaceLeft < tooltipWidth / 2 + 20) {
      // Próximo à borda esquerda - alinhar à esquerda
      align = 'left';
    } else {
      // Suficiente espaço em ambos os lados - centralizar
      align = 'center';
    }
    
    // Cálculo da posição X final, garantindo que o tooltip não ultrapasse os limites horizontais
    let x = barCenterX;
    
    if (align === 'center') {
      // Centralizado com ajustes para não extrapolar os limites
      const leftEdge = x - tooltipWidth / 2;
      const rightEdge = x + tooltipWidth / 2;
      
      if (leftEdge < visibleLeft + 10) {
        // Muito próximo à esquerda, reposicionar
        x = visibleLeft + tooltipWidth / 2 + 10;
      } else if (rightEdge > visibleRight - 10) {
        // Muito próximo à direita, reposicionar
        x = visibleRight - tooltipWidth / 2 - 10;
      }
    } else if (align === 'left') {
      // Alinhado à esquerda da barra
      if (x + tooltipWidth > visibleRight - 10) {
        // Não cabe à direita, forçar alinhamento à direita
        align = 'right';
      }
    } else if (align === 'right') {
      // Alinhado à direita da barra
      if (x - tooltipWidth < visibleLeft + 10) {
        // Não cabe à esquerda, forçar alinhamento à esquerda
        align = 'left';
      }
    }
    
    // Cálculo da posição Y final
    let y = showAbove ? barTop : barBottom;
    
    // Ajuste vertical para não extrapolar os limites
    if (showAbove && y - tooltipHeight < 10) {
      // Não há espaço suficiente acima, mostrar abaixo
      y = barBottom;
      showAbove = false;
    } else if (!showAbove && y + tooltipHeight > containerHeight - 10) {
      // Não há espaço suficiente abaixo, mostrar acima
      y = barTop;
      showAbove = true;
    }
    // Garante que não vamos atualizar o tooltip em cada movimento do mouse
    // apenas se houver mudança na posição ou no conteúdo
    const tooltipContent = {
      nome: item.nome,
      start: item.start,
      previsao: item.previsaoEntrega,
      end: item.dataConclusao,
      hasDelay: item.hasDelay,
      delayDays: item.hasDelay ? Math.ceil((item.dataConclusao - item.previsaoEntrega) / (1000 * 60 * 60 * 24)) : 0
    };
    
    // Verificamos se o tooltip já está visível e se estamos no mesmo projeto
    // Se estiver, não atualizamos a posição para evitar flickering
    if (tooltip.visible && tooltip.content && tooltip.content.nome === tooltipContent.nome) {
      // Apenas atualizamos o conteúdo, mantendo a mesma posição
      setTooltip(prev => ({
        ...prev,
        content: tooltipContent
      }));
    } else {
      // Primeira vez mostrando este tooltip, definimos posição e conteúdo
      setTooltip({
        visible: true,
        x,
        y,
        showAbove,
        align,
        content: tooltipContent
      });
    }
  }
  function handleBarMouseLeave() {
    // Suaviza a saída do tooltip
    setTooltip(prev => ({ 
      ...prev, 
      visible: false 
    }));
    
    // Limpa completamente após a animação
    setTimeout(() => {
      setTooltip(prev => {
        if (!prev.visible) {
          return { visible: false, x: 0, y: 0, content: null };
        }
        return prev;
      });
    }, 150); // tempo igual à duração da transition no CSS
  }

  // Funções para edição de itens
  function handleBarClick(item, barType = 'main') {
    // Abre modal de edição no clique simples
    if (onEditItem) {
      const originalItem = data.find(dataItem => dataItem['Nome'] === item.nome);
      if (originalItem) {
        setEditingItem(originalItem);
        setShowEditModal(true);
      }
    }
  }

  // Funções para edição de itens
  function handleBarDoubleClick(item) {
    // Duplo clique sem implementação por enquanto
    // Pode ser usado futuramente para outras funcionalidades
  }

  function handleEditSave(updatedItem) {
    if (onEditItem) {
      onEditItem(updatedItem);
    }
    setEditingItem(null);
    setShowEditModal(false);
  }

  function handleEditCancel() {
    setEditingItem(null);
    setShowEditModal(false);
  }

  // Função para calcular progresso do projeto
  const calculateProgress = (item) => {
    const start = parseDate(item['Data de Inicio']);
    const previsaoEntrega = parseDate(item['Previsão de Entrega']);
    const dataConclusao = parseDate(item['Data de Conclusão']);
    
    if (dataConclusao) return 100; // Concluído
    if (!start || !previsaoEntrega) return 0; // Sem datas válidas
    
    const today = new Date();
    if (today < start) return 0; // Não iniciado
    
    const totalDuration = previsaoEntrega.getTime() - start.getTime();
    const elapsedDuration = today.getTime() - start.getTime();
    
    if (elapsedDuration >= totalDuration) return 100; // Passou do prazo
    
    return Math.round((elapsedDuration / totalDuration) * 100);
  };

  // Função para ordenar dados da tabela
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Dados ordenados para a tabela
  const sortedTableData = useMemo(() => {
    if (!sortConfig.key) return flattenedData;
    
    return [...flattenedData].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'nome':
          aValue = a['Nome']?.toLowerCase() || '';
          bValue = b['Nome']?.toLowerCase() || '';
          break;
        case 'status':
          aValue = a['Status']?.toLowerCase() || '';
          bValue = b['Status']?.toLowerCase() || '';
          break;
        case 'dataInicio':
          aValue = parseDate(a['Data de Inicio'])?.getTime() || 0;
          bValue = parseDate(b['Data de Inicio'])?.getTime() || 0;
          break;
        case 'previsaoEntrega':
          aValue = parseDate(a['Previsão de Entrega'])?.getTime() || 0;
          bValue = parseDate(b['Previsão de Entrega'])?.getTime() || 0;
          break;
        case 'dataConclusao':
          aValue = parseDate(a['Data de Conclusão'])?.getTime() || 0;
          bValue = parseDate(b['Data de Conclusão'])?.getTime() || 0;
          break;
        case 'progresso':
          aValue = calculateProgress(a);
          bValue = calculateProgress(b);
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [flattenedData, sortConfig]);

  // Função para calcular a posição da linha "hoje" no gráfico usando timeline expandida
  function getTodayLinePosition() {
    // CORREÇÃO: Usar expandedPeriodLabels (que é array) ou visiblePeriodLabels (que tem start/end)
    if (!expandedPeriodLabels || expandedPeriodLabels.length === 0) {
      console.log('  ❌ expandedPeriodLabels não disponível');
      return 0;
    }
    
    const firstPeriod = expandedPeriodLabels[0];
    const lastPeriod = expandedPeriodLabels[expandedPeriodLabels.length - 1];
    
    if (!firstPeriod || !lastPeriod || !firstPeriod.start || !lastPeriod.end) {
      console.log('  ❌ firstPeriod ou lastPeriod não disponível');
      return 0;
    }
    
    const visibleStart = firstPeriod.start;
    const visibleEnd = lastPeriod.end;
    
    // Cria a data de hoje padronizada (meio-dia UTC para consistência)
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 12, 0, 0));
    
    // DEBUG: Log para verificar as datas
    console.log('📅 LINHA HOJE DEBUG (CORRIGIDO):');
    console.log('  Today:', today.toISOString(), '(25 jul 2025 = 2025-07-25)');
    console.log('  VisibleStart:', visibleStart.toISOString());
    console.log('  VisibleEnd:', visibleEnd.toISOString());
    console.log('  BaseDate:', baseDate.toISOString());
    console.log('  Scale:', scale, 'PeriodLength:', periodLength);
    console.log('  Visible Periods:', expandedPeriodLabels.length);
    
    // Verifica se "hoje" está dentro do range visível
    if (today < visibleStart) {
      console.log('  ❌ Today é anterior ao visibleStart');
      return 0;
    }
    if (today > visibleEnd) {
      console.log('  ❌ Today é posterior ao visibleEnd');
      return 100;
    }
    
    // Calcula a posição como percentual entre as datas visíveis
    const total = visibleEnd.getTime() - visibleStart.getTime();
    const left = ((today.getTime() - visibleStart.getTime()) / total) * 100;
    
    console.log('  ✅ Posição calculada (corrigida):', left.toFixed(2) + '%');
    console.log('  📊 Total duration (ms):', total);
    console.log('  📊 Today offset (ms):', today.getTime() - visibleStart.getTime());
    
    // Verificação: Onde julho deveria estar?
    const julyStart = new Date(Date.UTC(2025, 6, 1)); // 1º de julho
    const julyPosition = ((julyStart.getTime() - visibleStart.getTime()) / total) * 100;
    console.log('  🔍 Julho deveria começar em:', julyPosition.toFixed(2) + '%');
    console.log('  🔍 25 de julho deveria estar em:', (julyPosition + (24/31) * (100/12)).toFixed(2) + '%');
    
    // Garantir que o valor esteja entre 0 e 100
    return Math.max(0, Math.min(100, left));
  }

  // Função para verificar se "hoje" está dentro do período visível usando timeline visível
  function isTodayVisible() {
    // CORREÇÃO: Usar expandedPeriodLabels (que é array) 
    if (!expandedPeriodLabels || expandedPeriodLabels.length === 0) {
      console.log('📅 isTodayVisible - expandedPeriodLabels não disponível:', {
        isArray: Array.isArray(expandedPeriodLabels),
        length: expandedPeriodLabels?.length,
        type: typeof expandedPeriodLabels
      });
      return false;
    }
    
    const firstPeriod = expandedPeriodLabels[0];
    const lastPeriod = expandedPeriodLabels[expandedPeriodLabels.length - 1];
    
    if (!firstPeriod || !lastPeriod || !firstPeriod.start || !lastPeriod.end) {
      console.log('📅 isTodayVisible - firstPeriod ou lastPeriod inválido:', {
        firstPeriod,
        lastPeriod,
        hasFirstStart: !!firstPeriod?.start,
        hasLastEnd: !!lastPeriod?.end,
        expandedPeriodsLength: expandedPeriodLabels.length
      });
      return false;
    }
    
    const visibleStart = firstPeriod.start;
    const visibleEnd = lastPeriod.end;
    
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 12, 0, 0));
    
    console.log('📅 isTodayVisible DEBUG:');
    console.log('  Today:', today.toISOString());
    console.log('  VisibleStart:', visibleStart.toISOString());
    console.log('  VisibleEnd:', visibleEnd.toISOString());
    console.log('  Today >= VisibleStart:', today >= visibleStart);
    console.log('  Today <= VisibleEnd:', today <= visibleEnd);
    
    // Debugging: Check timezone and date calculations
    console.log('  Today components:', {
      year: today.getUTCFullYear(),
      month: today.getUTCMonth() + 1, // 0-based, so +1 for display
      date: today.getUTCDate(),
      time: today.getTime()
    });
    console.log('  VisibleStart components:', {
      year: visibleStart.getUTCFullYear(),
      month: visibleStart.getUTCMonth() + 1,
      date: visibleStart.getUTCDate(),
      time: visibleStart.getTime()
    });
    console.log('  VisibleEnd components:', {
      year: visibleEnd.getUTCFullYear(),
      month: visibleEnd.getUTCMonth() + 1,
      date: visibleEnd.getUTCDate(),
      time: visibleEnd.getTime()
    });
    
    // "Hoje" é visível se estiver dentro do range visível
    const isVisible = today >= visibleStart && today <= visibleEnd;
    console.log('  📅 isTodayVisible Result:', isVisible);
    
    return isVisible;
  }

  // Função para obter status temporal
  // A função getTemporalStatus foi removida

  // Opções de período pré-definidas
  const periodOptions = [
    { label: "Todos os períodos", value: "all" },
    { label: "Este mês", value: "thisMonth" },
    { label: "Próximo mês", value: "nextMonth" },
    { label: "Últimos 3 meses", value: "last3Months" },
    { label: "Próximos 3 meses", value: "next3Months" },
    { label: "Este ano", value: "thisYear" },
    { label: "Período customizado", value: "custom" }
  ];

  // Função para calcular a prioridade de ordenação de uma coluna
  const getColumnSortPriority = (status, data) => {
    const projectsWithStatus = data.filter(item => item['Status'] === status);
    if (projectsWithStatus.length === 0) return 2; // meio por padrão
    
    let concludedCount = 0;
    let notStartedCount = 0;
    let inProgressCount = 0;
    
    projectsWithStatus.forEach(item => {
      const start = parseDate(item['Data de Inicio']);
      const dataConclusao = parseDate(item['Data de Conclusão']);
      const today = new Date();
      
      if (dataConclusao) {
        concludedCount++;
      } else if (!start || start > today) {
        notStartedCount++;
      } else {
        inProgressCount++;
      }
    });
    
    const total = projectsWithStatus.length;
    const concludedRatio = concludedCount / total;
    const notStartedRatio = notStartedCount / total;
    
    // Se mais de 50% está concluído, vai para o final (prioridade 3)
    if (concludedRatio > 0.5) return 3;
    
    // Se mais de 50% não começou, vai para o início (prioridade 1)
    if (notStartedRatio > 0.5) return 1;
    
    // Caso contrário, fica no meio (prioridade 2)
    return 2;
  };

  // Organiza dados por status para o Kanban
  const kanbanColumns = useMemo(() => {
    const columns = {};
    
    // Inicializa colunas com todos os status disponíveis (exceto "-")
    availableStatuses.forEach(status => {
      columns[status] = {
        title: status,
        color: getFilterColorForStatus(status, data, customStatusColors),
        items: [],
        sortPriority: getColumnSortPriority(status, data)
      };
    });
    
    // Adiciona uma coluna especial para itens sem status definido
    const itemsWithoutStatus = flattenedData.filter(item => item['Status'] === '-');
    if (itemsWithoutStatus.length > 0) {
      columns['Sem Status'] = {
        title: 'Sem Status',
        color: '#64748b', // cor cinza
        items: [],
        sortPriority: 999 // vai para o final
      };
    }
    
    // Adiciona uma coluna especial para itens concluídos
    const concludedItems = flattenedData.filter(item => item['Data de Conclusão']);
    if (concludedItems.length > 0) {
      columns['Concluídos'] = {
        title: 'Concluídos',
        color: '#22c55e', // cor verde
        items: [],
        sortPriority: 1 // alta prioridade
      };
    }
    
    // Distribui os projetos filtrados pelas colunas
    flattenedData.forEach(item => {
      const status = item['Status'];
      const targetColumn = status === '-' ? 'Sem Status' : status;
      
      if (columns[targetColumn]) {
        const start = parseDate(item['Data de Inicio']);
        const dataConclusao = parseDate(item['Data de Conclusão']);
        const previsaoEntrega = parseDate(item['Previsão de Entrega']);
        const hasDelay = dataConclusao && previsaoEntrega && dataConclusao > previsaoEntrega;
        
        columns[targetColumn].items.push({
          nome: item['Nome'],
          status: item['Status'],
          start,
          dataConclusao,
          previsaoEntrega,
          hasDelay,
          color: getProjectColor({ status: item['Status'], start, dataConclusao }, customStatusColors),
          responsavel: item['Responsável'] || 'Não atribuído',
          prioridade: item['Prioridade'] || 'Normal'
        });
      }
    });
    
    // Ordena os itens dentro de cada coluna
    Object.values(columns).forEach(column => {
      column.items.sort((a, b) => {
        // Se um tem data de conclusão e outro não, o concluído vai para o final
        if (a.dataConclusao && !b.dataConclusao) return 1;
        if (!a.dataConclusao && b.dataConclusao) return -1;
        
        // Se ambos estão concluídos, ordena por data de conclusão (mais recente primeiro)
        if (a.dataConclusao && b.dataConclusao) {
          return b.dataConclusao.getTime() - a.dataConclusao.getTime();
        }
        
        // Se nenhum está concluído, ordena por data de início (mais próximo do prazo primeiro)
        const aDate = a.previsaoEntrega || a.start;
        const bDate = b.previsaoEntrega || b.start;
        
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        
        return aDate.getTime() - bDate.getTime();
      });
    });
    
    // Remove colunas vazias e ordena por prioridade
    return Object.entries(columns)
      .filter(([_, column]) => column.items.length > 0)
      .sort(([keyA, columnA], [keyB, columnB]) => {
        // Primeiro ordena por prioridade (1 = início, 2 = meio, 3 = fim)
        if (columnA.sortPriority !== columnB.sortPriority) {
          return columnA.sortPriority - columnB.sortPriority;
        }
        // Em caso de empate, ordena alfabeticamente
        return keyA.localeCompare(keyB);
      })
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
  }, [availableStatuses, flattenedData, data]);

  return (
    <div className="roadmap-chart-container w-full max-w-6xl mx-auto bg-white rounded-lg shadow-lg p-1 sm:p-6 mt-8 mb-2 px-0 sm:px-2 flex flex-col items-stretch relative">
      {/* Header Principal do Roadmap */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
        <div className="flex flex-col">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h2 className="roadmap-main-title">
              {currentRoadmapName ? `Editando: ${currentRoadmapName}` : 'Roadmap'}
            </h2>
          </div>
          
          {/* Informações adicionais */}
          <div className="flex items-center gap-4" style={{ 
            fontFamily: "'Inter', sans-serif",
            fontSize: '0.875rem',
            color: '#6b7280',
            fontWeight: 500
          }}>
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {flattenedData.filter(item => !item.isSubitem).length} itens
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              {flattenedData.filter(item => item.isSubitem).length} subitens visíveis
            </span>
            {hierarchicalData.some(item => item.subitems && item.subitems.length > 0 && !expandedItems.has(item.id)) && (
              <span className="flex items-center gap-1 text-amber-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L12 12m6-3a9.97 9.97 0 01-1.563 3.029M12 12L3 3m9 9l6-6" />
                </svg>
                {data.filter(item => item.level === 1).length - flattenedData.filter(item => item.isSubitem).length} ocultos
              </span>
            )}
          </div>
        </div>
        
        {/* Controles de Visualização e Ações */}
        <div className="flex items-center gap-3">
          {/* Toggle de visualização */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('gantt')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'gantt' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              📊 Gantt
            </button>
            {ENABLE_KANBAN_VIEW && (
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'kanban' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                📋 Kanban
              </button>
            )}
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              📄 Lista
            </button>
          </div>
          
          {/* Controles de Navegação Temporal - removidos da posição anterior */}
          
          {/* Botão de exportar */}
          <button
            onClick={handleExportClick}
            className="px-3 py-1.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center gap-2 text-sm font-medium shadow-sm hover:shadow-md"
            title="Exportar roadmap"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Exportar
          </button>

          {/* Botão Meus Roadmaps (se disponível) */}
          {onMyRoadmaps && (
            <button
              onClick={onMyRoadmaps}
              className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200 flex items-center gap-2 text-sm font-medium shadow-sm hover:shadow-md"
              title="Ver meus roadmaps"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
              </svg>
              Meus Roadmaps
            </button>
          )}
        </div>
      </div>
      
      {/* Filtros - Layout em Cards Organizados */}
      <div className="mb-6">
        {/* Header Elegante dos Filtros */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Filtros e Controles</h3>
              <p className="text-sm text-gray-600">Configure a visualização do roadmap</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {(selectedStatuses.size < availableStatuses.length || selectedPeriod !== 'all') && (
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-blue-700">
                  {flattenedData.length} de {data.length} itens exibidos
                </span>
              </div>
            )}
            
            {/* Botão Expandir/Colapsar */}
            <button
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-lg transition-all duration-200 group"
              title={`${filtersExpanded ? 'Ocultar' : 'Mostrar'} filtros`}
            >
              <svg 
                className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${filtersExpanded ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              </svg>
              <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                {filtersExpanded ? 'Ocultar' : 'Mostrar'}
              </span>
            </button>
          </div>
        </div>

        {/* Resumo dos Filtros Ativos quando Colapsado */}
        {!filtersExpanded && (selectedStatuses.size < availableStatuses.length || selectedStatuses.size === availableStatuses.length || selectedPeriod !== 'all' || (viewMode === 'gantt' && (!showTodayLine || scale !== 'month' || periodLength !== 12))) && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg animate-fadeIn">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                <span className="text-sm font-medium text-gray-700">Filtros ativos:</span>
              </div>
              
              {/* Badge Status - Filtrados ou Todos */}
              {selectedStatuses.size < availableStatuses.length ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs font-medium">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                  </svg>
                  {selectedStatuses.size} Status
                </span>
              ) : availableStatuses.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-md text-xs font-medium">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Todos os Status
                </span>
              )}
              
              {selectedPeriod !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-100 text-cyan-700 rounded-md text-xs font-medium">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {periodOptions.find(p => p.value === selectedPeriod)?.label}
                </span>
              )}
              
              {/* Badge Escala de Tempo (apenas Gantt) */}
              {viewMode === 'gantt' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 rounded-md text-xs font-medium">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {scale === 'month' ? 'Mensal' : 
                   scale === 'quarter' ? 'Trimestral' : 
                   scale === 'semester' ? 'Semestral' : 'Anual'}
                </span>
              )}
              
              {/* Badge Períodos (apenas Gantt) */}
              {viewMode === 'gantt' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 rounded-md text-xs font-medium">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  {periodLength} {scale === 'month' ? 'meses' : 
                               scale === 'quarter' ? 'trimestres' : 
                               scale === 'semester' ? 'semestres' : 'anos'}
                </span>
              )}
              
              {viewMode === 'gantt' && !showTodayLine && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-md text-xs font-medium">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  Linha Hoje Oculta
                </span>
              )}
            </div>
          </div>
        )}

        {/* Grid de Cards Organizados */}
        {filtersExpanded && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 animate-fadeIn">
            
            {/* Card 1: Filtros de Conteúdo */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
              </svg>
              <h4 className="text-sm font-semibold text-gray-900">Filtros de Conteúdo</h4>
            </div>
            
            {/* Status Filter */}
            {availableStatuses.length > 0 && (
              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-700">Status dos Projetos</label>
                <div className="relative status-dropdown-container">
                  <button
                    onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-200"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">
                        {selectedStatuses.size === availableStatuses.length ? 'Todos os Status' : `${selectedStatuses.size} Status`}
                      </span>
                      {selectedStatuses.size < availableStatuses.length && (
                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
                          {selectedStatuses.size}
                        </span>
                      )}
                    </div>
                    <svg 
                      className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isStatusDropdownOpen ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown Content */}
                  {isStatusDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-64 overflow-y-auto">
                      {/* Header do Dropdown */}
                      <div className="p-3 border-b border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-semibold text-gray-900">Filtrar por Status</h3>
                        </div>
                        
                        {/* Actions Row */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={toggleAll}
                            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded-md hover:bg-blue-50 transition-colors"
                          >
                            {selectedStatuses.size === availableStatuses.length ? (
                              <>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Desmarcar todos
                              </>
                            ) : (
                              <>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                Selecionar todos
                              </>
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Status Options */}
                      <div className="p-3">
                        <div className="space-y-1">
                          {availableStatuses.map(status => (
                            <label
                              key={status}
                              className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group"
                            >
                              <input
                                type="checkbox"
                                checked={selectedStatuses.has(status)}
                                onChange={() => toggleStatus(status)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 transition-colors"
                              />
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span
                                  className="px-2 py-1 rounded-md text-xs font-semibold text-white flex-shrink-0"
                                  style={{
                                    backgroundColor: getFilterColorForStatus(status, data, customStatusColors),
                                  }}
                                >
                                  {status}
                                </span>
                                <span className="text-xs text-gray-600 font-medium">
                                  {data.filter(item => item['Status'] === status).length} itens
                                </span>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Card 2: Configurações de Aparência */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <h4 className="text-sm font-semibold text-gray-900">Aparência</h4>
            </div>
            
            <div className="space-y-3">
              {/* Controle de Densidade */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Densidade Visual</label>
                <div className="grid grid-cols-3 gap-1 bg-gray-100 rounded-lg p-0.5">
                  <button
                    onClick={() => setDensity('compact')}
                    className={`px-2 py-1.5 rounded text-xs font-medium transition-all duration-200 flex items-center justify-center ${
                      density === 'compact' 
                        ? 'bg-white text-blue-700 shadow-sm font-semibold' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                    title="Visualização compacta"
                  >
                    <span style={{ fontSize: '10px', lineHeight: '1.1' }}>Aa</span>
                  </button>
                  <button
                    onClick={() => setDensity('comfortable')}
                    className={`px-2 py-1.5 rounded text-xs font-medium transition-all duration-200 flex items-center justify-center ${
                      density === 'comfortable' 
                        ? 'bg-white text-blue-700 shadow-sm font-semibold' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                    title="Visualização confortável"
                  >
                    <span style={{ fontSize: '12px', lineHeight: '1.1' }}>Aa</span>
                  </button>
                  <button
                    onClick={() => setDensity('spacious')}
                    className={`px-2 py-1.5 rounded text-xs font-medium transition-all duration-200 flex items-center justify-center ${
                      density === 'spacious' 
                        ? 'bg-white text-blue-700 shadow-sm font-semibold' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                    title="Visualização ampla"
                  >
                    <span style={{ fontSize: '14px', lineHeight: '1.1' }}>Aa</span>
                  </button>
                </div>
              </div>

              {/* Personalização de Cores */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Cores dos Status</label>
                <button
                  onClick={() => setShowColorCustomizer(true)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-lg hover:from-purple-100 hover:to-purple-200 transition-all duration-200 group"
                  title="Personalizar cores dos status"
                >
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                  </svg>
                  <span className="text-sm font-medium text-purple-700 group-hover:text-purple-800">
                    Personalizar Cores
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Card 3: Controles de Timeline (apenas Gantt) */}
          {viewMode === 'gantt' && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h4 className="text-sm font-semibold text-gray-900">Timeline</h4>
              </div>
              
              <div className="space-y-3">
                {/* Seletor de Escala */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Escala de Tempo</label>
                  <div className="grid grid-cols-4 gap-0.5 bg-gray-100 rounded-lg p-0.5">
                    {[
                      { value: 'month', label: 'Mês', icon: '📅' },
                      { value: 'quarter', label: 'Tri', icon: '📊' },
                      { value: 'semester', label: 'Sem', icon: '📈' },
                      { value: 'year', label: 'Ano', icon: '🗓️' }
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => setScale(option.value)}
                        className={`px-1.5 py-1.5 rounded text-xs font-medium transition-all duration-200 flex flex-col items-center justify-center gap-0.5 ${
                          scale === option.value
                            ? 'bg-white text-blue-700 shadow-sm font-semibold'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                        title={`Visualizar por ${option.label.toLowerCase()}`}
                      >
                        <span className="text-sm leading-none">{option.icon}</span>
                        <span className="text-xs leading-none">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Seletor de Períodos */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Número de Períodos</label>
                  <select 
                    value={periodLength} 
                    onChange={(e) => setPeriodLength(Number(e.target.value))}
                    className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  >
                    {getZoomOptions(scale).map(option => (
                      <option key={option} value={option}>
                        {option} {scale === 'month' ? 'meses' : 
                               scale === 'quarter' ? 'trimestres' : 
                               scale === 'semester' ? 'semestres' : 'anos'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Toggle Linha Hoje */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Indicador "Hoje"</label>
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">Linha "Hoje"</span>
                    </div>
                    
                    {/* Toggle Switch */}
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={showTodayLine}
                        onChange={() => setShowTodayLine(!showTodayLine)}
                        className="sr-only peer" 
                      />
                      <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 hover:bg-gray-300 peer-checked:hover:bg-blue-700 transition-colors duration-200"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Banner de Período Personalizado */}
        {filtersExpanded && selectedPeriod === 'custom' && (
          <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-blue-900">Período Personalizado</h4>
                  <p className="text-xs text-blue-700">Configure as datas de início e fim do roadmap</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-blue-700">De:</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="px-3 py-1.5 border border-blue-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-blue-700">Até:</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="px-3 py-1.5 border border-blue-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {flattenedData.length === 0 ? (
        <p className="text-gray-500">Nenhum dado para exibir.</p>
      ) : viewMode === 'gantt' ? (
        <div ref={chartAreaRef} className="w-full">
          {/* Container principal - SEM scroll, apenas o container da timeline terá scroll */}
          <div
            className={`${isExporting ? "border border-gray-100 rounded-xl bg-gradient-to-br from-white to-gray-50/30 shadow-lg" : "border border-gray-100 rounded-xl bg-gradient-to-br from-white to-gray-50/30 shadow-lg"} transition-all duration-150`}
            style={{ 
              width: '100%', 
              position: 'relative', 
              contain: 'layout style',
              overflow: 'hidden' // Remove scroll deste container
            }}
            title="Use os controles de navegação ou scroll para navegar no tempo"
          >
          {/* Cabeçalho dos períodos (escala) */}
          <div
            className={`flex flex-row items-center sticky top-0 bg-gradient-to-r from-gray-50 to-white z-20 border-b border-gray-100 backdrop-blur-sm transition-all duration-150`}
            style={{ 
              minHeight: getDensityConfig().headerHeight, 
              height: getDensityConfig().headerHeight, 
              width: '100%', 
              overflow: 'visible',
              display: 'flex',
              alignItems: 'center'
            }}
            ref={(el) => {
              chartContainerRef.current = el;
              // scrollContainerRef deve apontar para o container pai, não este elemento
            }}
          >
            {/* Coluna fixa "Itens" - NÃO move com scroll */}
            <div
              style={{
                ...getStickyStyles({
                  width: NAME_COL_WIDTH,
                  minWidth: NAME_COL_WIDTH,
                  maxWidth: NAME_COL_WIDTH,
                  zIndex: STICKY_CONFIG.HEADER_Z_INDEX,
                  background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                  height: getDensityConfig().headerHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: '1px solid #e5e7eb',
                  paddingLeft: 12,
                  paddingRight: 8,
                  boxSizing: 'border-box',
                  fontSize: `${getDensityConfig().fontSize.header}px`
                })
              }}
              className="roadmap-section-title"
            >
              {/* Container principal com layout flexível */}
              <div style={{ 
                width: '100%', 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                position: 'relative',
                paddingLeft: '12px',
                paddingRight: '8px'
              }}>
                {/* Texto "Itens" à esquerda */}
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  flexShrink: 0
                }}>
                  <span style={{ 
                    display: 'block',
                    lineHeight: '1.2',
                    margin: 0,
                    padding: 0,
                    fontFamily: "'Inter', sans-serif",
                    fontSize: `${getDensityConfig().fontSize.header}px`,
                    fontWeight: 600,
                    color: '#374151',
                    letterSpacing: '-0.025em'
                  }}>Itens</span>
                </div>
                
                {/* Controles posicionados à direita */}
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0
                }}>
                  <div className="flex items-center gap-1">
                {/* Controles de Expand/Collapse Global */}
                {hierarchicalData.some(item => item.subitems && item.subitems.length > 0) && (
                  <>
                    <button
                      onClick={expandAllItems}
                      className="w-6 h-6 rounded-md flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-all active:scale-95"
                      title="Expandir todos os itens"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                    
                    <button
                      onClick={collapseAllItems}
                      className="w-6 h-6 rounded-md flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-all active:scale-95"
                      title="Colapsar todos os itens"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                      </svg>
                    </button>
                    
                    <div className="w-px h-4 bg-gray-300 mx-1"></div>
                  </>
                )}
                
                {/* Controles de Navegação Temporal */}
                <button
                  onClick={navigatePrevious}
                  className="w-6 h-6 rounded-md flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-all active:scale-95"
                  title={`Navegar para ${scale === 'month' ? 'mês' : scale === 'quarter' ? 'trimestre' : scale === 'semester' ? 'semestre' : 'ano'} anterior`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <button
                  onClick={navigateNext}
                  className="w-6 h-6 rounded-md flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-all active:scale-95"
                  title={`Navegar para próximo ${scale === 'month' ? 'mês' : scale === 'quarter' ? 'trimestre' : scale === 'semester' ? 'semestre' : 'ano'}`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                  </div>
                </div>
              </div>
            </div>
            {/* Container da timeline com scroll horizontal */}
            <div 
              ref={(el) => {
                scrollRef.current = el;
                scrollContainerRef.current = el; // Container de scroll para medição de offset
              }}
              className="force-scrollbar"
              onMouseDown={handleMouseDown}
              style={{ 
                width: `calc(100% - ${NAME_COL_WIDTH}px)`,
                height: '100%',
                overflowX: 'scroll',
                overflowY: 'hidden',
                scrollbarWidth: 'auto', // Firefox
                msOverflowStyle: 'auto', // IE
                WebkitOverflowScrolling: 'touch', // iOS
                display: 'block', // Força block display
                position: 'relative', // Garante posicionamento
                scrollbarGutter: 'stable both-edges', // CSS moderno
                cursor: isDragging ? 'grabbing' : 'grab', // Cursor visual para drag
                margin: 0,
                padding: 0,
                boxSizing: 'border-box',
                left: 0,
                top: 0,
                border: 'none',
                outline: 'none'
              }}
            >
              <div 
                className={`flex flex-row`}
                style={{ 
                  width: expandedTimelineWidth, // Use expandedTimelineWidth directly for consistency
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  zIndex: STICKY_CONFIG.CONTENT_Z_INDEX,
                  margin: 0,
                  padding: 0,
                  boxSizing: 'border-box',
                  position: 'relative',
                  left: 0,
                  top: 0
                }}
              >
                {/* 🔍 DEBUG: Verificando posição do container do header */}
                {process.env.NODE_ENV === 'development' && (
                  <div 
                    style={{
                      position: 'absolute',
                      top: '-20px',
                      left: '10px',
                      backgroundColor: 'rgba(0, 0, 255, 0.8)',
                      color: 'white',
                      fontSize: '10px',
                      padding: '2px 4px',
                      borderRadius: '2px',
                      whiteSpace: 'nowrap',
                      zIndex: 1000,
                      pointerEvents: 'none'
                    }}
                  >
                    HEADER: expanded={expandedTimelineWidth}px | available={availableWidth}px | periods={expandedPeriodLabels.length}
                  </div>
                )}
                {expandedPeriodLabels.map((p, idx) => {
                  const columnWidth = Math.floor(expandedTimelineWidth / expandedPeriodLabels.length);
                  return (
                  <div
                    key={`header-${p.label}-${idx}-${p.start?.getTime()}-${p.end?.getTime()}`}
                    style={{ 
                      fontWeight: 600, 
                      color: '#374151', 
                      fontSize: `${getDensityConfig().fontSize.header}px`,
                      width: `${columnWidth}px`,
                      minWidth: `${columnWidth}px`,
                      maxWidth: `${columnWidth}px`,
                      flexShrink: 0,
                      height: `${getDensityConfig().headerHeight}px`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'visible',
                      lineHeight: '1.2',  // Ajustado para melhor legibilidade
                      margin: 0,
                      padding: 0,
                      paddingBottom: '8px',  // Espaçamento inferior maior
                      boxSizing: 'border-box',
                      position: 'relative',
                      left: 0,
                      top: 0,
                      borderRight: idx === expandedPeriodLabels.length - 1 ? 'none' : '1px solid #e5e7eb',
                      borderLeft: 'none',
                      borderTop: 'none',
                      borderBottom: 'none'
                    }}
                    ref={(el) => {
                      // Debug: Medir tamanho de cada célula do cabeçalho
                      if (el) {
                        const rect = el.getBoundingClientRect();
                        const computedStyle = window.getComputedStyle(el);
                        console.log(`🔍 HEADER ${idx + 1} (${p.label}):`, {
                          width: `${rect.width.toFixed(2)}px`,
                          offsetWidth: `${el.offsetWidth}px`,
                          clientWidth: `${el.clientWidth}px`,
                          computedWidth: computedStyle.width,
                          borderRight: computedStyle.borderRight,
                          position: `left: ${rect.left.toFixed(2)}px, right: ${rect.right.toFixed(2)}px`
                        });
                      }
                    }}
                  >
                    {/* Texto do período centralizado absolutamente */}
                    <div style={{ 
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span style={{ 
                        display: 'block',
                        lineHeight: '1.2',  // Ajustado para melhor legibilidade
                        margin: 0,
                        padding: 0,
                        paddingBottom: '4px',  // Espaçamento inferior adicional
                        fontFamily: "'Inter', sans-serif",
                        fontSize: `${getDensityConfig().fontSize.header}px`,
                        fontWeight: 600,
                        color: '#374151',
                        letterSpacing: '-0.025em'
                      }}>
                        {p.label}
                      </span>
                    </div>
                    
                    {/* Debug overlay - mostra tamanho da célula */}
                    {process.env.NODE_ENV === 'development' && (
                      <div 
                        style={{
                          position: 'absolute',
                          top: '-20px',
                          left: '2px',
                          backgroundColor: 'rgba(255, 0, 0, 0.8)',
                          color: 'white',
                          fontSize: '10px',
                          padding: '2px 4px',
                          borderRadius: '2px',
                          whiteSpace: 'nowrap',
                          zIndex: 1000,
                          pointerEvents: 'none'
                        }}
                      >
                        HEADER #{idx + 1}: {Math.floor(expandedTimelineWidth / expandedPeriodLabels.length)}px
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          </div>
          {/* Linhas do Gantt */}
          <div className="space-y-2" style={{ 
            position: 'relative', 
            zIndex: 5, 
            width: '100%', // Largura total disponível
            minHeight: '200px',
            overflowY: 'visible',
            overflowX: 'hidden' // Remove scroll horizontal desta div
          }}>
            
            {/* Container da linha "HOJE" - Sistema elegante e profissional */}
            {(() => {
              const conditions = {
                minDate: !!minDate,
                maxDate: !!maxDate,
                showTodayLine,
                isTodayVisible: isTodayVisible(),
                hasItems: items.length > 0
              };
              
              console.log('🔍 DEBUG LINHA HOJE - Condições:', conditions);
              if (minDate) console.log('  📅 minDate:', minDate.toISOString());
              if (maxDate) console.log('  📅 maxDate:', maxDate.toISOString());
              
              const shouldShow = minDate && maxDate && showTodayLine && isTodayVisible() && items.length > 0;
              console.log('  ✅ Deve mostrar linha hoje?', shouldShow);
              
              return shouldShow;
            })() && (
              <div
                className="absolute top-0 pointer-events-none"
                style={{
                  left: NAME_COL_WIDTH,
                  width: `calc(100% - ${NAME_COL_WIDTH}px)`,
                  height: '100%', // Usar 100% para cobrir todo o container
                  overflow: 'hidden',
                  zIndex: STICKY_CONFIG.TODAY_LINE_Z_INDEX
                }}
              >
                {/* Linha "HOJE" com posicionamento elegante */}
                <div
                  className="absolute top-0"
                  style={{
                    left: `${(expandedTimelineWidth * getTodayLinePosition() / 100) - scrollState.scrollLeft}px`,
                    width: '3px',
                    height: '100%',
                    background: 'linear-gradient(to bottom, #3b82f6 0%, #1d4ed8 50%, #1e40af 100%)',
                    boxShadow: '0 0 8px rgba(59, 130, 246, 0.6), 0 0 16px rgba(59, 130, 246, 0.3)',
                    borderRadius: '1.5px'
                  }}
                >
                  {/* Indicador circular elegante no topo */}
                  <div
                    className="absolute -top-2 -left-2 w-7 h-7 rounded-full shadow-xl flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.5), 0 2px 4px rgba(0, 0, 0, 0.1)',
                      border: '2px solid rgba(255, 255, 255, 0.9)'
                    }}
                  >
                    <div className="w-2 h-2 bg-white rounded-full shadow-sm"></div>
                  </div>
                  
                  {/* Label "HOJE" elegante */}
                  <div
                    className="absolute px-3 py-1.5 text-white text-xs font-bold rounded-lg shadow-lg backdrop-blur-sm"
                    style={{
                      top: '28px',
                      left: '-14px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      fontSize: '10px',
                      whiteSpace: 'nowrap',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      letterSpacing: '0.5px'
                    }}
                  >
                    HOJE
                  </div>
                </div>
              </div>
            )}
            
            {items.map((item, idx) => (
              <div key={`${item.nome}-${idx}-${baseDate.getTime()}-${scale}`} className="flex flex-row items-center relative group hover:bg-gradient-to-r hover:from-blue-50/20 hover:to-white transition-all duration-200" style={{ 
                width: '100%',
                height: `${getDensityConfig().rowHeight}px`,
                backgroundColor: item.isSubitem ? '#f8fafc' : 'white',
                contain: 'layout style'
              }}>
                {/* Linha divisória abaixo de cada projeto (exceto o último) */}
                {idx < items.length - 1 && (
                  <div
                    className="absolute bottom-0 left-0 w-full border-b transition-colors duration-200"
                    style={{
                      borderBottomColor: item.isSubitem ? '#f1f5f9' : '#e5e7eb',
                      borderBottomWidth: '1px',
                      zIndex: 10,
                      transform: 'translateY(2px)',
                      opacity: 0.6
                    }}
                  />
                )}
                
                {/* Coluna fixa de nomes - NÃO move com scroll */}
                <div
                  style={{
                    ...getStickyStyles({
                      zIndex: STICKY_CONFIG.ITEM_COLUMN_Z_INDEX,
                      background: item.isSubitem ? '#f8fafc' : 'white',
                      height: `${getDensityConfig().rowHeight}px`,
                      width: NAME_COL_WIDTH,
                      minWidth: NAME_COL_WIDTH,
                      maxWidth: NAME_COL_WIDTH,
                      paddingLeft: item.isSubitem ? getDensityConfig().padding.subitemIndent : getDensityConfig().padding.horizontal,
                      paddingRight: getDensityConfig().padding.horizontal / 2,
                      paddingTop: Math.max(getDensityConfig().padding.vertical, 2),  // Mínimo de 2px
                      paddingBottom: Math.max(getDensityConfig().padding.vertical, 2),  // Mínimo de 2px
                      display: 'flex',
                      alignItems: 'center',  // Mudado de 'flex-start' para 'center'
                      justifyContent: 'center',
                      overflow: 'visible',
                      flexDirection: 'column',
                      borderRight: '1px solid #e5e7eb',
                      boxSizing: 'border-box'
                    })
                  }}
                >
                  <div className="flex flex-col w-full">
                    {/* Linha do título - agora com botão de expand/collapse */}
                    <div className="flex items-center gap-1 flex-1 min-w-0 mb-1">
                      {/* Botão de expand/collapse para itens pais */}
                      {!item.isSubitem && (
                        <ExpandCollapseButton
                          isExpanded={item.isExpanded}
                          hasChildren={item.hasChildren}
                          onClick={() => toggleItemExpansion(item.id)}
                          itemName={item.nome || item.Nome}
                        />
                      )}
                      
                      <span
                        className={`truncate ${
                          item.isParent 
                            ? 'roadmap-project-title' 
                            : item.isSubitem 
                              ? 'roadmap-subactivity-title' 
                              : 'roadmap-activity-title'
                        }`}
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: NAME_COL_WIDTH - (item.isSubitem ? 36 : 24), // Mais espaço disponível
                          fontSize: item.isParent 
                            ? `${getDensityConfig().fontSize.project}px`
                            : item.isSubitem 
                              ? `${getDensityConfig().fontSize.subactivity}px`
                              : `${getDensityConfig().fontSize.activity}px`
                        }}
                        title={sanitizeText(item.nome)}
                      >
                        {sanitizeText(item.nome)}
                      </span>
                    </div>
                    
                    {/* Linha do status com ícones de ação */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium shadow-sm"
                          style={{
                            background: getProjectBarColor(item, 'main', customStatusColors),
                            color: '#fff',
                            minWidth: 60,
                            maxWidth: 100,
                            fontSize: '10px',
                            display: item.status === '-' ? 'none' : 'inline-flex',
                          }}
                        >
                          {sanitizeText(item.status)}
                        </span>
                      </div>
                      
                      {/* Ícones de ação - agora na linha do status */}
                      <div className="flex items-center gap-1" style={{ zIndex: 60, position: 'relative' }}>
                        {!isExporting && onEditItem && (
                          <button
                            className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-md flex-shrink-0 transition-all duration-200 hover:shadow-sm transform hover:scale-105"
                            title="Editar item"
                            onClick={() => handleBarClick(item)}
                            data-no-drag="true"
                            style={{ zIndex: 65 }}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                        {!isExporting && onRemoveItem && (
                          <button
                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md flex-shrink-0 transition-all duration-200 hover:shadow-sm transform hover:scale-105"
                            title="Remover item"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveClick(item);
                            }}
                            data-no-drag="true"
                            style={{ zIndex: 65 }}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Container da timeline - move com scroll */}
                <div 
                  className={`absolute`}
                  style={{ 
                    left: NAME_COL_WIDTH,
                    width: `calc(100% - ${NAME_COL_WIDTH}px)`,
                    height: '28px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    overflow: 'hidden'
                  }}
                >
                  {/* Container de scroll para grid - alinhado com header */}
                  <div 
                    style={{
                      width: '100%',
                      height: '100%',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                  >
                    {/* Linhas de grade verticais para alinhar com as colunas do cabeçalho */}
                    <div 
                      className="absolute inset-0 flex"
                      style={{
                        left: -scrollState.scrollLeft, // Simplificado - apenas scrollLeft sem offset adicional
                        width: expandedTimelineWidth, // Use expandedTimelineWidth for consistency with header
                        marginLeft: 0,
                        transform: 'translateZ(0)' // Force hardware acceleration
                      }}
                      ref={(el) => {
                        if (el) {
                          console.log('🎯 GRID POSITION UPDATE:', { 
                            scrollLeft: scrollState.scrollLeft, 
                            calculatedLeft: -scrollState.scrollLeft,
                            actualLeft: el.style.left 
                          });
                        }
                      }}
                    >
                    {/* 🔍 DEBUG: Verificando posição do container do grid */}
                    {process.env.NODE_ENV === 'development' && (
                      <div 
                        style={{
                          position: 'absolute',
                          top: '-40px',
                          left: '10px',
                          backgroundColor: 'rgba(0, 255, 0, 0.8)',
                          color: 'white',
                          fontSize: '10px',
                          padding: '2px 4px',
                          borderRadius: '2px',
                          whiteSpace: 'nowrap',
                          zIndex: 1000,
                          pointerEvents: 'none'
                        }}
                      >
                        GRID Left: {-scrollState.scrollLeft}px | ScrollLeft: {scrollState.scrollLeft}px
                      </div>
                    )}
                    {expandedPeriodLabels.map((period, idx) => {
                      const columnWidth = Math.floor(expandedTimelineWidth / expandedPeriodLabels.length);
                      return (
                      <div 
                        key={`grid-${idx}-${period.start?.getTime()}-${period.end?.getTime()}`} 
                        style={{ 
                          width: `${columnWidth}px`, // Largura fixa para todas as colunas
                          minWidth: `${columnWidth}px`, // MinWidth igual à largura
                          maxWidth: `${columnWidth}px`, // MaxWidth igual à largura
                          flexShrink: 0,
                          margin: 0,
                          padding: 0,
                          boxSizing: 'border-box',
                          borderRight: idx === expandedPeriodLabels.length - 1 ? 'none' : '1px solid #f1f5f9',
                          borderLeft: 'none',
                          borderTop: 'none',
                          borderBottom: 'none'
                        }}
                        ref={(el) => {
                          // Debug: Medir tamanho de cada célula do GRID
                          if (el) {
                            const rect = el.getBoundingClientRect();
                            const computedStyle = window.getComputedStyle(el);
                            console.log(`🔍 GRID ${idx + 1} (${period.label}):`, {
                              width: `${rect.width.toFixed(2)}px`,
                              offsetWidth: `${el.offsetWidth}px`,
                              clientWidth: `${el.clientWidth}px`,
                              computedWidth: computedStyle.width,
                              borderRight: computedStyle.borderRight,
                              position: `left: ${rect.left.toFixed(2)}px, right: ${rect.right.toFixed(2)}px`
                            });
                          }
                        }}
                      />
                      );
                    })}
                  </div>
                  
                  {/* NOVA RENDERIZAÇÃO SIMPLES DE BARRAS */}
                  {item.hasValidDates && (
                    <div
                      className="absolute rounded-lg opacity-80 hover:opacity-100 cursor-pointer transition-opacity shadow-sm"
                      style={{
                        ...getItemBarStyle(item),
                        backgroundColor: getProjectBarColor(item, 'main', customStatusColors),
                        height: `${getDensityConfig().barHeight}px`,
                        top: '50%',
                        transform: 'translateY(-50%)'
                      }}
                      onMouseEnter={e => handleBarMouseEnter(e, item)}
                      onMouseLeave={handleBarMouseLeave}
                      onClick={() => handleBarClick(item, 'main')}
                      onDoubleClick={() => handleBarDoubleClick(item)}
                      title={`${item.nome}: ${item.start?.toLocaleDateString('pt-BR')} - ${item.end?.toLocaleDateString('pt-BR')}`}
                    >
                    </div>
                  )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Tooltip customizado - otimizado para evitar reposicionamento visual */}
          {tooltip.visible && tooltip.content && (
        <div
          className="fixed z-50 bg-white border border-gray-300 rounded-lg shadow-lg px-4 py-2 text-xs text-gray-700 pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y + (tooltip.showAbove ? -10 : 10),
            transform:
              tooltip.align === 'right'
                ? (tooltip.showAbove ? 'translate(-100%, -100%)' : 'translate(-100%, 0)')
                : tooltip.align === 'left'
                ? (tooltip.showAbove ? 'translate(0, -100%)' : 'translate(0, 0)')
                : (tooltip.showAbove ? 'translate(-50%, -100%)' : 'translate(-50%, 0)'),
            minWidth: 240,
            maxWidth: 320,
            whiteSpace: 'pre-line',
            willChange: 'transform',
            overflow: 'hidden',
            contain: 'layout paint',
            boxSizing: 'border-box',
            transition: 'opacity 0.15s ease-in-out',
            opacity: 1,
          }}
            >
              <div className="font-semibold text-sm mb-1">{tooltip.content.nome}</div>
              <div><span className="font-medium">Data de início:</span> {tooltip.content.start ? formatDateBR(tooltip.content.start) : '-'}</div>
              <div><span className="font-medium">Previsão de Entrega:</span> {tooltip.content.previsao ? formatDateBR(tooltip.content.previsao) : '-'}</div>
              <div><span className="font-medium">Data de conclusão:</span> {tooltip.content.end ? formatDateBR(tooltip.content.end) : '-'}</div>
              {tooltip.content.hasDelay && (
                <div className="mt-1 pt-1 border-t border-gray-300">
                  <span className="font-medium text-red-600">Atraso:</span> 
                  <span className="text-red-600 font-semibold"> {tooltip.content.delayDays} dia{tooltip.content.delayDays !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      ) : viewMode === 'kanban' ? (
        /* Visualização Kanban */
        <div className="kanban-container" ref={chartAreaRef}>
          <div className="flex gap-6 pb-6 min-h-96">
            {Object.entries(kanbanColumns).map(([statusKey, column]) => (
              <div 
                key={statusKey} 
                className="flex-1 min-w-72 bg-gray-50 rounded-lg border border-gray-200"
              >
                {/* Cabeçalho da coluna */}
                <div 
                  className="p-4 border-b border-gray-200 rounded-t-lg"
                  style={{ backgroundColor: column.color + '15' }}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: column.color }}
                      />
                      {column.title}
                    </h3>
                    <span className="bg-white text-gray-600 text-xs font-medium px-2 py-1 rounded-full">
                      {column.items.length}
                    </span>
                  </div>
                </div>
                
                {/* Cards dos projetos */}
                <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                  {column.items.map((item, idx) => (
                    <div 
                      key={item.nome + idx}
                      className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                    >
                      {/* Nome do projeto */}
                      <h4 className="font-medium text-gray-900 mb-2 line-clamp-2">
                        {item.nome}
                      </h4>
                      
                      {/* Informações de data */}
                      <div className="space-y-1 text-xs text-gray-600 mb-3">
                        {item.start && (
                          <div className="flex items-center gap-1">
                            <span className="text-gray-500">📅 Início:</span>
                            <span>{formatDateBR(item.start)}</span>
                          </div>
                        )}
                        {item.previsaoEntrega && (
                          <div className="flex items-center gap-1">
                            <span className="text-gray-500">🎯 Previsão:</span>
                            <span>{formatDateBR(item.previsaoEntrega)}</span>
                          </div>
                        )}
                        {item.dataConclusao && (
                          <div className="flex items-center gap-1">
                            <span className="text-gray-500">✅ Concluído:</span>
                            <span>{formatDateBR(item.dataConclusao)}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Status visual e atraso */}
                      <div className="flex items-center justify-between">
                        <div 
                          className="px-2 py-1 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: item.color }}
                        >
                          {item.dataConclusao ? 'Concluído' : item.start && new Date() > item.start ? 'Em andamento' : 'Agendado'}
                        </div>
                        
                        {item.hasDelay && (
                          <div className="flex items-center gap-1 text-red-600 text-xs font-medium">
                            <span>⚠️</span>
                            <span>Atraso</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Responsável (se existir) */}
                      {item.responsavel && item.responsavel !== 'Não atribuído' && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <div className="flex items-center gap-1 text-xs text-gray-600">
                            <span>👤</span>
                            <span>{item.responsavel}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {column.items.length === 0 && (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      Nenhum projeto neste status
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {Object.keys(kanbanColumns).length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Nenhum dado para exibir no Kanban
            </div>
          )}
        </div>
      ) : (
        /* Visualização Lista */
        <div className="table-container" ref={chartAreaRef}>
          <div className={isExporting ? "border rounded-lg bg-white shadow-lg" : "overflow-x-auto border rounded-lg bg-white shadow-lg"}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr style={{ height: `${getDensityConfig().headerHeight}px` }}>
                  <th 
                    className="text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    style={{
                      paddingLeft: `${getDensityConfig().padding.horizontal}px`,
                      paddingRight: `${getDensityConfig().padding.horizontal}px`,
                      paddingTop: `${getDensityConfig().padding.vertical * 2}px`,
                      paddingBottom: `${getDensityConfig().padding.vertical * 2}px`,
                      fontSize: `${getDensityConfig().fontSize.header}px`
                    }}
                    onClick={() => handleSort('nome')}
                  >
                    <div className="flex items-center gap-1">
                      Itens
                      {sortConfig.key === 'nome' && (
                        <span className="text-blue-600">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    style={{
                      paddingLeft: `${getDensityConfig().padding.horizontal}px`,
                      paddingRight: `${getDensityConfig().padding.horizontal}px`,
                      paddingTop: `${getDensityConfig().padding.vertical * 2}px`,
                      paddingBottom: `${getDensityConfig().padding.vertical * 2}px`,
                      fontSize: `${getDensityConfig().fontSize.header}px`
                    }}
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      Status
                      {sortConfig.key === 'status' && (
                        <span className="text-blue-600">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    style={{
                      paddingLeft: `${getDensityConfig().padding.horizontal}px`,
                      paddingRight: `${getDensityConfig().padding.horizontal}px`,
                      paddingTop: `${getDensityConfig().padding.vertical * 2}px`,
                      paddingBottom: `${getDensityConfig().padding.vertical * 2}px`,
                      fontSize: `${getDensityConfig().fontSize.header}px`
                    }}
                    onClick={() => handleSort('dataInicio')}
                  >
                    <div className="flex items-center gap-1">
                      Data de Início
                      {sortConfig.key === 'dataInicio' && (
                        <span className="text-blue-600">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    style={{
                      paddingLeft: `${getDensityConfig().padding.horizontal}px`,
                      paddingRight: `${getDensityConfig().padding.horizontal}px`,
                      paddingTop: `${getDensityConfig().padding.vertical * 2}px`,
                      paddingBottom: `${getDensityConfig().padding.vertical * 2}px`,
                      fontSize: `${getDensityConfig().fontSize.header}px`
                    }}
                    onClick={() => handleSort('previsaoEntrega')}
                  >
                    <div className="flex items-center gap-1">
                      Previsão de Entrega
                      {sortConfig.key === 'previsaoEntrega' && (
                        <span className="text-blue-600">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    style={{
                      paddingLeft: `${getDensityConfig().padding.horizontal}px`,
                      paddingRight: `${getDensityConfig().padding.horizontal}px`,
                      paddingTop: `${getDensityConfig().padding.vertical * 2}px`,
                      paddingBottom: `${getDensityConfig().padding.vertical * 2}px`,
                      fontSize: `${getDensityConfig().fontSize.header}px`
                    }}
                    onClick={() => handleSort('dataConclusao')}
                  >
                    <div className="flex items-center gap-1">
                      Data de Conclusão
                      {sortConfig.key === 'dataConclusao' && (
                        <span className="text-blue-600">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status do Prazo
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedTableData.map((item, idx) => {
                  const start = parseDate(item['Data de Inicio']);
                  const previsaoEntrega = parseDate(item['Previsão de Entrega']);
                  const dataConclusao = parseDate(item['Data de Conclusão']);
                  const progresso = calculateProgress(item);
                  const hasDelay = dataConclusao && previsaoEntrega && dataConclusao > previsaoEntrega;
                  const isLate = !dataConclusao && previsaoEntrega && new Date() > previsaoEntrega;
                  
                  return (
                    <tr 
                      key={item['Nome'] + idx} 
                      className={`hover:bg-gray-50 ${item.isSubitem ? 'bg-slate-50' : ''}`}
                      style={{ height: `${getDensityConfig().rowHeight}px` }}
                    >
                      <td 
                        className="whitespace-nowrap" 
                        style={{
                          paddingLeft: `${getDensityConfig().padding.horizontal}px`,
                          paddingRight: `${getDensityConfig().padding.horizontal}px`,
                          paddingTop: `${getDensityConfig().padding.vertical * 4}px`,
                          paddingBottom: `${getDensityConfig().padding.vertical * 4}px`
                        }}
                      >
                        <div 
                          className="flex items-center"
                          style={{
                            paddingLeft: item.isSubitem ? `${getDensityConfig().padding.subitemIndent}px` : '0px'
                          }}
                        >
                          {/* Container para o botão de expand/collapse - garante alinhamento */}
                          <div className="w-5 flex justify-start mr-1">
                            {!item.isSubitem && (
                              <ExpandCollapseButton
                                isExpanded={item.isExpanded}
                                hasChildren={item.hasChildren}
                                onClick={() => toggleItemExpansion(item.id)}
                                itemName={item.nome || item.Nome}
                              />
                            )}
                          </div>
                          
                          <div 
                            className={`${
                              item.isParent 
                                ? 'font-semibold text-gray-900' 
                                : item.isSubitem 
                                  ? 'font-normal text-gray-700' 
                                  : 'font-medium text-gray-900'
                            }`}
                            style={{
                              fontSize: `${
                                item.isParent 
                                  ? getDensityConfig().fontSize.project 
                                  : item.isSubitem 
                                    ? getDensityConfig().fontSize.subactivity 
                                    : getDensityConfig().fontSize.activity
                              }px`
                            }}
                          >
                            {item['Nome']}
                          </div>
                        </div>
                      </td>
                      <td 
                        className="whitespace-nowrap" 
                        style={{
                          paddingLeft: `${getDensityConfig().padding.horizontal}px`,
                          paddingRight: `${getDensityConfig().padding.horizontal}px`,
                          paddingTop: `${getDensityConfig().padding.vertical * 4}px`,
                          paddingBottom: `${getDensityConfig().padding.vertical * 4}px`
                        }}
                      >
                        {item['Status'] !== '-' && (
                          <span 
                            className="inline-flex px-2 py-1 font-semibold rounded-full text-white"
                            style={{ 
                              backgroundColor: getFilterColorForStatus(item['Status'], data, customStatusColors),
                              fontSize: `${getDensityConfig().fontSize.activity - 1}px`
                            }}
                          >
                            {item['Status']}
                          </span>
                        )}
                      </td>
                      <td 
                        className="whitespace-nowrap text-gray-900" 
                        style={{
                          paddingLeft: `${getDensityConfig().padding.horizontal}px`,
                          paddingRight: `${getDensityConfig().padding.horizontal}px`,
                          paddingTop: `${getDensityConfig().padding.vertical * 4}px`,
                          paddingBottom: `${getDensityConfig().padding.vertical * 4}px`,
                          fontSize: `${getDensityConfig().fontSize.activity}px`
                        }}
                      >
                        {start ? formatDateBR(start) : '-'}
                      </td>
                      <td 
                        className="whitespace-nowrap text-gray-900" 
                        style={{
                          paddingLeft: `${getDensityConfig().padding.horizontal}px`,
                          paddingRight: `${getDensityConfig().padding.horizontal}px`,
                          paddingTop: `${getDensityConfig().padding.vertical * 4}px`,
                          paddingBottom: `${getDensityConfig().padding.vertical * 4}px`,
                          fontSize: `${getDensityConfig().fontSize.activity}px`
                        }}
                      >
                        {previsaoEntrega ? formatDateBR(previsaoEntrega) : '-'}
                      </td>
                      <td 
                        className="whitespace-nowrap text-gray-900" 
                        style={{
                          paddingLeft: `${getDensityConfig().padding.horizontal}px`,
                          paddingRight: `${getDensityConfig().padding.horizontal}px`,
                          paddingTop: `${getDensityConfig().padding.vertical * 4}px`,
                          paddingBottom: `${getDensityConfig().padding.vertical * 4}px`,
                          fontSize: `${getDensityConfig().fontSize.activity}px`
                        }}
                      >
                        {dataConclusao ? formatDateBR(dataConclusao) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {dataConclusao ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            ✅ Concluído{hasDelay && ' (com atraso)'}
                          </span>
                        ) : isLate ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            ⚠️ Atrasado
                          </span>
                        ) : previsaoEntrega && new Date() <= previsaoEntrega ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            ⏳ No prazo
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                            📋 Sem prazo
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {sortedTableData.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                Nenhum dado para exibir na tabela
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Modal de confirmação */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl p-6 mx-4 max-w-md w-full border border-gray-200">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg mr-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900">Exportar Roadmap</h3>
            </div>
            
            <p className="text-gray-600 mb-4">
              Escolha o formato para exportar o roadmap:
            </p>
            
            <div className="mb-6 space-y-3">
              <label className="flex items-center cursor-pointer p-3 rounded-lg border-2 border-gray-200 hover:border-blue-300 transition-all duration-200 hover:shadow-sm">
                <input
                  type="radio"
                  name="exportType"
                  value="png"
                  checked={exportType === 'png'}
                  onChange={(e) => setExportType(e.target.value)}
                  className="mr-3 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <div className="font-semibold text-gray-900">PNG</div>
                  <div className="text-sm text-gray-600">Imagem do gráfico visual</div>
                </div>
              </label>
              <label className="flex items-center cursor-pointer p-3 rounded-lg border-2 border-gray-200 hover:border-blue-300 transition-all duration-200 hover:shadow-sm">
                <input
                  type="radio"
                  name="exportType"
                  value="xlsx"
                  checked={exportType === 'xlsx'}
                  onChange={(e) => setExportType(e.target.value)}
                  className="mr-3 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <div className="font-semibold text-gray-900">XLSX</div>
                  <div className="text-sm text-gray-600">Planilha com dados do roadmap</div>
                </div>
              </label>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelExport}
                className="px-6 py-2.5 text-gray-600 hover:text-gray-800 font-medium transition-all duration-200 rounded-lg hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={confirmExport}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 font-medium transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
              >
                {exportType === 'png' ? 'Baixar Imagem' : 'Baixar Planilha'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de confirmação de remoção */}
      {showRemoveConfirmModal && itemToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl p-6 mx-4 max-w-md w-full border border-gray-200">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg mr-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900">Remover Item</h3>
            </div>
            
            <p className="text-gray-600 mb-2">
              Tem certeza que deseja remover <strong>"{itemToRemove.Nome || itemToRemove.nome || 'Item'}"</strong>?
            </p>
            
            {itemToRemove.level === 0 && (
              <p className="text-red-600 text-sm mb-4 bg-gradient-to-r from-red-50 to-orange-50 p-3 rounded-lg border border-red-200 shadow-sm">
                <span className="font-medium">⚠️ Atenção:</span> Este item principal será removido junto com todos os seus subitens.
              </p>
            )}
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={cancelRemove}
                className="px-6 py-2.5 text-gray-600 hover:text-gray-800 font-medium transition-all duration-200 rounded-lg hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={confirmRemove}
                className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 font-medium transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
              >
                Remover
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Indicador durante redimensionamento */}
      {isResizing && resizingItem && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-3 rounded-lg shadow-lg z-50 max-w-md">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm font-medium">
              Redimensionando: {resizingItem.Nome || resizingItem.nome}
            </span>
            <div className="text-xs bg-blue-700 px-2 py-1 rounded">
              {resizeType === 'start' ? 'Início' : 'Fim'}
            </div>
          </div>
          <div className="flex gap-4 text-xs">
            <div>
              <span className="text-blue-200">Início:</span> {formatDateForDisplay(resizingItem.start)}
            </div>
            <div>
              <span className="text-blue-200">Fim:</span> {formatDateForDisplay(resizingItem.end || resizingItem.dataConclusao)}
            </div>
          </div>
          <div className="text-xs text-blue-200 mt-1 italic">
            As mudanças são aplicadas automaticamente ao soltar
          </div>
        </div>
      )}
      
      {/* Modal de personalização de cores */}
      {showColorCustomizer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl p-5 mx-4 max-w-lg w-full border border-gray-200 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5H9a2 2 0 00-2 2v12a4 4 0 004 4h10V5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Personalizar Cores</h3>
                  <p className="text-xs text-gray-600">Customize as cores dos status</p>
                </div>
              </div>
              <button
                onClick={() => setShowColorCustomizer(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-3 mb-5">
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Status Encontrados</h4>
              
              {availableStatuses.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <svg className="w-10 h-10 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a.997.997 0 01-.707.293H7a4 4 0 01-4-4V7a4 4 0 014-4z" />
                  </svg>
                  <p className="text-xs">Nenhum status encontrado</p>
                </div>
              ) : (
                <div className="grid gap-2.5">
                  {availableStatuses.map(status => (
                    <div 
                      key={status}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-6 h-6 rounded-md shadow-sm border border-gray-200"
                          style={{ backgroundColor: getCurrentStatusColor(status) }}
                        />
                        <div>
                          <span className="font-medium text-gray-900 text-sm">{status}</span>
                          <div className="text-xs text-gray-500">
                            {data.filter(item => item['Status'] === status).length} item{data.filter(item => item['Status'] === status).length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={getCurrentStatusColor(status)}
                          onChange={(e) => updateStatusColor(status, e.target.value)}
                          className="w-7 h-7 rounded border border-gray-300 cursor-pointer hover:border-gray-400 transition-colors"
                          title={`Escolher cor para ${status}`}
                        />
                        {customStatusColors[status] && (
                          <button
                            onClick={() => {
                              const newColors = { ...customStatusColors };
                              delete newColors[status];
                              saveCustomColors(newColors);
                            }}
                            className="text-gray-400 hover:text-red-500 transition-colors p-1"
                            title="Resetar para cor padrão"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {availableStatuses.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-700">Customizadas:</span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                      {Object.keys(customStatusColors).length}/{availableStatuses.length}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={resetStatusColors}
                      disabled={Object.keys(customStatusColors).length === 0}
                      className="px-3 py-1.5 text-gray-600 hover:text-gray-800 font-medium transition-all duration-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      Resetar
                    </button>
                    <button
                      onClick={() => setShowColorCustomizer(false)}
                      className="px-4 py-1.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 font-medium transition-all duration-200 shadow-md hover:shadow-lg text-sm"
                    >
                      Concluído
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* O modal de ajuda do zoom foi removido */}
      {/* Modal de edição de item do roadmap */}
      {showEditModal && editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-2xl w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Editar item do roadmap</h3>
            <RoadmapItemEditor
              item={editingItem}
              onSave={handleEditSave}
              onCancel={handleEditCancel}
              isEditing={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Adicionar estilos CSS para animação
const styleSheet = document.createElement('style');
styleSheet.innerHTML = `
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-in-out;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// Adicionar apenas uma vez
if (!document.head.querySelector('[data-roadmap-chart-styles]')) {
  styleSheet.setAttribute('data-roadmap-chart-styles', 'true');
  document.head.appendChild(styleSheet);
}

export default RoadmapChart;
